import type {
  ActivityRow,
  AdvancePermission,
  BlockReleaseKind,
  BlockOptions,
  BlockTargetOption,
  StageListing as StageListingData,
  DateAuthority,
  DateChangeReason,
  DateChangeRequestView,
  DeliverableView,
  DeploymentOption,
  ReferenceOption,
  StageOutcome,
  TaskDetail as TaskDetailData,
  TaskStageView,
  TaskStatus,
  TaskView,
} from "@fieldpath/shared";
import { ajeraKey, daysOverdue, isPendingRequest, mdy, mdyDay, mdyRange, unreadyFix } from "@fieldpath/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import { api, ApiError, type Fetcher } from "../api/client";
import { useRefreshOnReturn } from "../refreshOnReturn";
import { schedulerUrl } from "../config";
import { navigate } from "../router";
import { overdueText, taskDaysOverdue } from "../overdue";
import { ruleActivity } from "../ruleActors";
import { HelpButton } from "./HelpButton";
import { CancelTaskControl } from "./CancelToggle";
import { RequestPanel, RequestedChip } from "./RequestPanel";
import { Crumbs } from "./Crumbs";
import { PAST_DATE_BOX, todayLocal } from "./DateHint";
import { RichText, toggleCheckAtLine } from "./RichText";
import { SearchSelect } from "./SearchSelect";
import { StageEditor } from "./StageEditor";
import { takeOpenInstructions } from "./openInstructions";
import { SubjectFields } from "./FieldsPanel";
import { StageListing } from "./StageListing";
import { StagePlan } from "./StagePlan";
import { TaskNotes } from "./TaskNotes";
import { KindTag } from "./KindTag";
import { Modal } from "./Modal";

/**
 * The statuses a simple task is moved between, in order (punch list #25).
 * Each button reads the status it sets: "Complete" set Done, and the page
 * then said Done (22 Oct 2026).
 */
const SIMPLE_STATUSES: readonly TaskStatus[] = ["Not Started", "In Progress", "Done"];

/**
 * Everything the Block dialog decides, in one object.
 *
 * Not five positional arguments. The two nullable ids sit next to each other,
 * and `onBlock(true, reason, hibernate, kind, waitsOn, snooze)` is a call that
 * is wrong the first time somebody edits it.
 *
 * `releaseKind` is carried even when it is `manual`, because `waitsOnStageId:
 * null` cannot distinguish "somebody will release this by hand" from "waiting
 * on a task nobody has linked yet" — decision E2.15, and the whole reason the
 * kind is a column rather than an inference.
 */
type BlockRequest = {
  reason: string;
  reasonKey: string | null;
  hibernate: boolean;
  releaseKind: BlockReleaseKind;
  waitsOnStageId: string | null;
  snoozeUntil: string | null;
};
import { EditChip } from "./EditChip";

interface Props {
  fetcher: Fetcher;
  task: TaskView;
  /** This task's address, for handing to someone else. */
  shareUrl: string;
  /** For the trail. Absent only while the parent is still loading it. */
  projectName?: string | null;
  onBack: () => void;
}

/**
 * Copy the task's link, for pasting into Teams.
 *
 * Confirms it worked. Clipboard writes are silent, and a button that appears
 * to do nothing gets pressed repeatedly. The textarea path covers browsers
 * that refuse the async clipboard API outside a secure context — which
 * includes plain-http local development.
 */
function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const field = document.createElement("textarea");
      field.value = url;
      field.style.position = "fixed";
      field.style.opacity = "0";
      document.body.appendChild(field);
      field.select();
      try {
        document.execCommand("copy");
      } finally {
        document.body.removeChild(field);
      }
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button className="linkish" onClick={() => void copy()} title={url}>
      {copied ? "✓ Link copied" : "Copy link"}
    </button>
  );
}

export function TaskDetail({
  fetcher,
  task,
  shareUrl,
  projectName,
  onBack,
}: Props) {
  const [detail, setDetail] = useState<TaskDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * What the LAST advance released elsewhere.
   *
   * The advancing person is the one person the notification deliberately does
   * not reach -- `notify` filters the actor out -- and that rule assumes they
   * know what they did. Clicking Advance on a site visit and unblocking a
   * report in another deliverable is a consequence they may know nothing
   * about, so it is said here, on the screen they are already looking at.
   */
  const [released, setReleased] = useState<
    readonly { taskId: string; taskTitle: string }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [people, setPeople] = useState<ReferenceOption[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Skipped stages are hidden until asked for (Jeremy, 20 Oct 2026).
  const [showSkipped, setShowSkipped] = useState(false);
  const [showAllSteps, setShowAllSteps] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const [showMoveBack, setShowMoveBack] = useState(false);
  // Needed only to add yourself to a stage, which is the way past the
  // advance rule.
  const [me, setMe] = useState<{ ajeraKey: string } | null>(null);

  const reload = useCallback(
    () =>
      api
        .getTaskWorkflow(fetcher, task.id)
        .then(setDetail)
        .catch((e: Error) => setError(e.message)),
    [fetcher, task.id],
  );

  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(() => {
    void reload();
  }, [reload, back]);

  useEffect(() => {
    void api.employees(fetcher).then(setPeople).catch(() => undefined);
    void api
      .me(fetcher)
      .then((r) => setMe(r.employee ? { ajeraKey: r.employee.ajeraKey } : null))
      .catch(() => undefined);
  }, [fetcher]);

  // Follow the work: when the task moves on, the open tab moves with it. People
  // browsing another stage are not yanked away, only the stage they were
  // looking at is gone.
  const currentId = detail?.stages.find((s) => s.isCurrent)?.id ?? null;
  // Arrived from New personal task: its instructions open, ready to type.
  useEffect(() => {
    if (!currentId || !takeOpenInstructions(task.id)) return;
    setEditing(currentId);
    window.setTimeout(() => document.getElementById(`desc-${currentId}`)?.focus(), 0);
  }, [currentId, task.id]);
  useEffect(() => {
    setSelectedId((previous) =>
      previous && detail?.stages.some((s) => s.id === previous && !s.isCurrent)
        ? previous
        : currentId,
    );
  }, [currentId, detail]);

  /**
   * Run an action, reload, and surface a failure.
   *
   * GENERIC so the caller can see what came back. It took `() =>
   * Promise<unknown>` and discarded the value until 10 Oct 2026, which is why
   * the advance could not tell anybody what else it had done — the server had
   * been returning it all along.
   *
   * `undefined` on failure, because the catch already put the message on
   * screen and a caller reading the result should not also have to decide
   * what a thrown action returns.
   */
  const act = async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    setError(null);
    // Cleared on EVERY action, not just the next advance. A notice saying
    // "this also unblocked Habitat report" still sitting there after three
    // unrelated clicks reads as though it just happened again.
    setReleased([]);
    setBusy(true);
    try {
      const value = await fn();
      await reload();
      return value;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      return undefined;
    } finally {
      setBusy(false);
    }
  };

  if (!detail) return <p className="muted">Loading…</p>;

  // A cancelled task is kept as it stood until it is reinstated (23 Oct
  // 2026): the server refuses every change but a note, so nothing else is
  // offered. Its own axis, apart from `editability` -- that says who may,
  // this says nobody may, yet -- as a block is apart from the advance right.
  const cancelled = detail.task.cancelledAt !== null;

  const selected =
    detail.stages.find((s) => s.id === selectedId) ??
    detail.stages.find((s) => s.isCurrent) ??
    detail.stages[0] ??
    null;
  // The tabs on show: everything but skipped stages, unless asked for. The
  // current and the selected stage always stay, so nothing open disappears.
  const visibleStages = detail.stages.filter(
    (s) => showSkipped || !s.skipped || s.isCurrent || s.id === selectedId,
  );
  const skippedCount = detail.stages.filter((s) => s.skipped).length;
  const selectedIndex = selected ? visibleStages.findIndex((s) => s.id === selected.id) : -1;
  /** Look at another stage: the arrows' only effect (punch list #10). */
  const showStage = (index: number) => {
    const target = visibleStages[index];
    if (!target) return;
    setSelectedId(target.id);
    setEditing(null);
  };

  return (
    <>
      <div className="row spread" style={{ marginBottom: 12 }}>
        <button className="linkish" onClick={onBack}>
          ← Back to project
        </button>
        <div className="row">
          {/* Beside Copy link because both are things you do TO the task,
              rather than work you do on it. Cancelling is not an edit to a
              field and should not sit among the fields. Move back a stage
              sits on its stage's row in the stage list since F169. */}
          <CancelTaskControl
            cancelledAt={detail.task.cancelledAt}
            cancelReason={detail.task.cancelReason}
            mayCancel={detail.editability.cancel}
            onToggle={async (cancelled, reason) => {
              await api.setTaskCancelled(fetcher, task.id, cancelled, reason);
              await reload();
            }}
          />
          <CopyLink url={shareUrl} />
        </div>
      </div>


      {/* Title on the left, who is on it on the right. The roster is a
          reference you glance at, not something you read down the page, so
          it belongs beside the heading rather than pushing the actual work
          further down. */}
      <div className="task-head">
        <div className="task-head-main">
          <Crumbs
            projectId={detail.task.projectId}
            projectName={projectName}
            deliverableId={detail.deliverable?.id}
            deliverableName={detail.deliverable?.name}
          />

          <TaskTitle
            title={detail.task.title}
            mayRename={detail.editability.handOn && !cancelled}
            busy={busy}
            onRename={async (title) =>
              (await act(async () => {
                await api.updateTask(fetcher, detail.task.id, { title });
                return true;
              })) === true
            }
          />

          {/* A personal task has no deliverable (F174): nothing to show or change. */}
          {detail.editability.personal ? null : (
          <PartOf
            fetcher={fetcher}
            task={detail.task}
            current={detail.deliverable}
            mayMove={detail.editability.moveDeliverable && !cancelled}
            maySetDate={detail.editability.dueDate === "direct" && !cancelled}
            onChanged={() => void reload()}
          />
          )}

          <p className="muted" style={{ marginTop: 4, marginBottom: 0 }}>
            <span
              className={`state ${detail.task.status.toLowerCase().replace(" ", "_")}`}
            >
              {detail.task.status}
            </span>
            {detail.task.cancelledAt ? (
              // Next to the status rather than replacing it: the status is
              // still true -- the work really did get that far -- and
              // overwriting it would rewrite history to make the line tidier.
              <span className="chip cancelled" style={{ marginLeft: 6 }}>
                Cancelled
              </span>
            ) : null}
            {/* A request still waiting for a PM (punch list F140). */}
            {isPendingRequest(detail.task) ? <RequestedChip /> : null}
            {/* The TASK's own date. "Due to client" belongs to the
                deliverable (punch list #41, 20 Oct 2026). */}
            {detail.task.dueDate ? ` · Task final due ${mdy(detail.task.dueDate)}` : ""}
            {/* Said where the date is (punch list F150): before, a date
                weeks gone read exactly like one next month. */}
            <OverdueMark days={taskDaysOverdue(detail.task, todayLocal())} />
            {detail.workflowName ? ` · ${detail.workflowName}` : ""}
            {detail.task.figureTypeName ? ` · ${detail.task.figureTypeName}` : ""}
          </p>
        </div>

      </div>

      {/* Cancelled says so across the page, not only in a small chip by the
          buttons (punch list #28, 16 Oct 2026): a cancelled task that still
          reads "Not Started" looks like work nobody has begun. */}
      {detail.task.cancelledAt ? (
        <div className="cancelled-banner" role="status">
          <span className="cancelled-banner-tag">CANCELLED</span>
          <div>
            <strong>
              This task was cancelled on {mdyDay(detail.task.cancelledAt)}.
            </strong>
            {detail.task.cancelReason ? <> Reason: “{detail.task.cancelReason}”</> : null}
            <p className="muted" style={{ margin: "4px 0 0" }}>
              It is kept as it stood ({detail.task.status}) and is no longer counted as open work.
              Nothing on it changes until it is reinstated; notes can still be added.
              {detail.editability.cancel ? " Reinstate it at the top of the page to carry on." : ""}
            </p>
          </div>
        </div>
      ) : null}

      {/* Requested work (punch list F140): it waits at its first stage until
          somebody Deltek flags PM, or an admin, approves it. */}
      {isPendingRequest(detail.task) ? (
        <RequestPanel
          fetcher={fetcher}
          kind="tasks"
          id={detail.task.id}
          projectId={detail.task.projectId}
          mayDecide={detail.editability.decideRequest}
          onDecided={reload}
        />
      ) : null}

      {error ? <div className="error">{error}</div> : null}

      {/* WHAT ELSE THAT ADVANCE DID. Only ever rendered when there is
          something to say; an empty array is the ordinary case and renders
          nothing at all rather than a reassuring "nothing was unblocked". */}
      {released.length > 0 ? (
        <div className="release-notice">
          <span className="release-notice-tag">ALSO UNBLOCKED</span>
          <div>
            {released.map((r) => (
              <button
                key={r.taskId}
                className="linkish"
                onClick={() => navigate({ kind: "task", id: r.taskId })}
              >
                {r.taskTitle}
              </button>
            ))}
            <p className="muted" style={{ margin: "4px 0 0" }}>
              {released.length === 1 ? "This task was" : "These tasks were"} waiting
              on the stage you just reached.
            </p>
          </div>
        </div>
      ) : null}

      {/* Two columns. The work is on the left and the CONTEXT is on the right
          -- who is on it, where it is going, and what has already happened.
          All of that used to sit between the heading and the stage you are
          actually working on, which pushed the one thing people open this
          page to do below the fold on a laptop. */}
      <div className="task-wrap">
        <div className="task-main">

      {!detail.workflowName ? (
        <>
          {/* A simple task's status is set HERE (punch list #25, 16 Oct
              2026). This card said so and offered no way to do it. */}
          <div className="card">
            <p className="muted" style={{ marginTop: 0 }}>
              This task follows no workflow — its status is set here.
            </p>
            {detail.editability.status ? (
              <div className="row simple-status" role="group" aria-label="Status">
                {SIMPLE_STATUSES.map((value) => (
                  <button
                    key={value}
                    className={detail.task.status === value ? "primary" : "secondary"}
                    aria-pressed={detail.task.status === value}
                    disabled={busy}
                    onClick={() => {
                      if (detail.task.status === value) return;
                      void act(() => api.updateTask(fetcher, detail.task.id, { status: value }));
                    }}
                  >
                    {value}
                  </button>
                ))}
              </div>
            ) : detail.task.cancelledAt ? null : (
              // Everyone `maySetStatus` lets in: the deliverable's PM was
              // missing from this line until 22 Oct 2026.
              <p className="muted" style={{ marginBottom: 0 }}>
                The person it is assigned to, its PM / Task Manager, its project's PM or TM
                {detail.deliverable ? ", its deliverable's PM" : ""} or an admin changes it.
              </p>
            )}
          </div>
          <TaskNotes fetcher={fetcher} taskId={task.id} people={people} />
        </>
      ) : (
        <>
          {/* ONE ROW. A workflow is a SEQUENCE, and a grid that wraps reads
              as a set of tiles to choose between rather than a route work
              travels. Jeremy, 4 Oct 2026: "they read like tiles, they need to
              be in one row to denote a work flow".

              So each stage carries only its NAME and whether it is done. Who
              holds it and when it is due were here and are gone -- the side
              panel says both, and carrying them twice is what made each one
              wide enough to force the wrap in the first place.

              The bars across the top nearly meet, so they read as one
              progress meter spanning the whole job: four lime, one olive,
              five grey says "four done, here now, five to go" before a word
              is read. */}
          {/* Previous and next LOOK at a stage; they never start, advance or
              send work back (punch list #10, 12 Oct 2026). */}
          <div className="stage-nav">
            <button
              type="button"
              className="stage-nav-arrow"
              aria-label="Previous stage"
              title="Previous stage"
              disabled={selectedIndex <= 0}
              onClick={() => showStage(selectedIndex - 1)}
            >
              ‹
            </button>
          <div className="stage-strip" role="tablist" aria-label="Stages">
            {visibleStages.map((stage) => {
              const look = stageLook(stage, detail.task.status === "Done" || stage.skipped);
              return (
                <button
                  key={stage.id}
                  role="tab"
                  aria-selected={stage.id === selectedId}
                  // `blocked` from the BLOCK, not the state: a block stopped
                  // being a state on 10 Oct, and the strip went on asking the
                  // state -- so a blocked stage's bar never turned red. Found
                  // while adding the label below, 12 Oct 2026.
                  //
                  // `current` marks where the work IS, started or not
                  // (Jeremy, 20 Oct 2026): a current stage nobody had
                  // started looked like every other grey "Not started" tab.
                  className={`sstep ${look.className}${stage.isCurrent ? " current" : ""}${
                    stage.id === selectedId ? " active" : ""
                  }`}
                  onClick={() => {
                    setSelectedId(stage.id);
                    setEditing(null);
                  }}
                  title={`${stage.isCurrent ? "Current stage: " : ""}${stage.name} — ${look.label}`}
                >
                  {stage.isCurrent ? <span className="sstep-current-tag">Current stage</span> : null}
                  <span className="sstep-bar" aria-hidden="true" />
                  <span className="sstep-name">{stage.name}</span>
                  {/* The state in words under the name (punch list #9). The
                      bar's colour said it alone, which nobody can read
                      without a legend. */}
                  <span className="sstep-state">{look.label}</span>
                </button>
              );
            })}
          </div>
            <button
              type="button"
              className="stage-nav-arrow"
              aria-label="Next stage"
              title="Next stage"
              disabled={selectedIndex < 0 || selectedIndex >= visibleStages.length - 1}
              onClick={() => showStage(selectedIndex + 1)}
            >
              ›
            </button>
          </div>
          {/* Skipped stages are hidden; this brings them back (Jeremy, 20 Oct
              2026). Only shown when the route has skipped something. */}
          {skippedCount > 0 ? (
            <button type="button" className="linkish stage-skipped-toggle" onClick={() => setShowSkipped((v) => !v)}>
              {showSkipped
                ? "Hide skipped stages"
                : `Show skipped stages (${skippedCount})`}
            </button>
          ) : null}

          <div className="stage-panel">

            {selected ? (
              <Stage
                stage={selected}
                outcomes={detail.outcomes}
                people={people}
                clientDueDate={detail.deliverable?.clientDueDate ?? null}
                deliverableName={detail.deliverable?.name ?? null}
                requiresHours={detail.requiresHours}
                requiresDates={detail.requiresDates}
                listing={detail.listings[selected.id] ?? null}
                onAddItem={(input) => void act(() => api.addStageItem(fetcher, selected.id, input))}
                onRemoveItem={(itemId) => void act(() => api.removeStageItem(fetcher, itemId))}
                onSetItem={(itemId, input) => void act(() => api.setStageItem(fetcher, itemId, input))}
                onNumberItem={(itemId, n) => void act(() => api.setStageItemNumber(fetcher, itemId, n))}
                advance={detail.advance}
                mayHibernate={detail.mayHibernate}
                cancelled={cancelled}
                // The CURRENT stage only (F150): one the route skipped, or
                // the work has left, is not late whatever its date says.
                overdueDays={
                  selected.isCurrent && !selected.isEnd
                    ? daysOverdue(
                        selected.dueDate,
                        todayLocal(),
                        selected.state === "complete" || detail.task.status === "Done" || cancelled,
                      )
                    : null
                }
                onReadAll={() => setShowAllSteps(true)}
                error={error}
                onAddMe={() =>
                  void act(async () => {
                    // Adding yourself is the sanctioned way past the rule, so
                    // it is one button rather than a trip through Reassign.
                    const mine = me?.ajeraKey;
                    if (!mine) return;
                    await api.updateStage(fetcher, task.id, selected.id, {
                      assigneeKeys: [
                        ...selected.assignees.map((a) => a.key),
                        mine,
                      ],
                    });
                  })
                }
                busy={busy}
                isEditing={editing === selected.id}
                onToggleEdit={() =>
                  setEditing(editing === selected.id ? null : selected.id)
                }
                onSave={(body) =>
                  void act(async () => {
                    await api.updateStage(fetcher, task.id, selected.id, body);
                    setEditing(null);
                  })
                }
                dateAuthority={detail.editability.stageDates[selected.id] ?? "none"}
                fromBooking={detail.editability.schedulerOwnedStages.includes(selected.id)}
                mayAssignOthers={detail.editability.stageAssignOthers[selected.id] ?? false}
                mayChangeWork={detail.editability.stageWork[selected.id] ?? false}
                mayChangeInstructions={detail.editability.stageInstructions[selected.id] ?? false}
                onRequestDate={(body) =>
                  void act(async () => {
                    await api.requestDateChange(fetcher, task.id, {
                      taskStageId: selected.id,
                      ...body,
                    });
                    setEditing(null);
                  })
                }
                onStart={() => void act(() => api.startStage(fetcher, task.id))}
                onUndoStart={() => void act(() => api.undoStartStage(fetcher, task.id))}
                onBlock={(blocked, req) =>
                  void act(() => api.setBlocked(fetcher, task.id, blocked, req))
                }
                loadBlockTargets={() => api.getBlockTargets(fetcher, task.id)}
                loadBlockOptions={() => api.getBlockOptions(fetcher, task.id)}
                onSetBlockTarget={async (waitsOnStageId) =>
                  (await act(async () => {
                    await api.setBlockTarget(fetcher, task.id, waitsOnStageId);
                    return true;
                  })) === true
                }
                onComplete={(outcome, reason, pmNote, overrideUnready, acknowledgePastDue) =>
                  void act(async () => {
                    const res = await api.completeStage(
                      fetcher,
                      task.id,
                      outcome,
                      reason,
                      pmNote,
                      overrideUnready,
                      acknowledgePastDue,
                    );
                    setReleased(res.result.releasedBlocks);
                    return res;
                  }).then((res) => {
                    // Follow the work to the stage it moved to (punch list
                    // #52, 20 Oct 2026). The "don't yank someone browsing
                    // another stage" rule above was keeping the page on the
                    // stage just finished, since it is no longer current.
                    if (res) setSelectedId(null);
                  })
                }
              />
            ) : null}
            {/* The selected stage's custom fields (E3/4) -- the QA/QC audit
                lives here. Loaded and saved by the panel itself, through the
                same filtered read every surface uses. */}
            {selected ? (
              <SubjectFields
                fetcher={fetcher}
                subject={{ level: "stage", id: selected.id }}
                title={`${selected.name} — details`}
                locked
              />
            ) : null}
          </div>

          {/* Reading the whole job at once, directly after the stage you are
              working on. The tabs are right for DOING the work — one stage at
              a time — but wrong for picking it up cold, where you need the
              shape of the whole thing first. Ten clicks to read ten
              paragraphs is how people end up not reading them. */}
          {/* Both read and plan open as POP-UPS now (punch list #5, #6), not
              sections expanding below: they are a different view of the whole
              job, and opening one below the fold looked like nothing happened.
              "Read all instructions" is pressed from above the stage's own
              instructions, inside the stage panel. */}
          {showAllSteps ? (
            <Modal title="All instructions" wide onCancel={() => setShowAllSteps(false)}>
              <AllInstructions stages={detail.stages} inDialog />
            </Modal>
          ) : null}

          {showMoveBack ? (
            <MoveBackDialog
              busy={busy}
              onCancel={() => setShowMoveBack(false)}
              onMoveBack={(note) => {
                setShowMoveBack(false);
                void act(() => api.reverseStage(fetcher, task.id, note));
              }}
            />
          ) : null}

          {showPlan ? (
            <Modal title="Plan all stages" wide onCancel={() => setShowPlan(false)} help={["plan-all-stages", "task"]}>
              <StagePlan
                stages={detail.stages}
                fromBooking={detail.editability.schedulerOwnedStages}
                mayAssignOthers={detail.editability.stageAssignOthers}
                stageWork={detail.editability.stageWork}
                stageDates={detail.editability.stageDates}
                meKey={me?.ajeraKey ?? null}
                requiresHours={detail.requiresHours}
                requiresDates={detail.requiresDates}
                people={people}
                busy={busy}
                onSave={async (stageId, body) => {
                  await api.updatePlannedStage(fetcher, task.id, stageId, body);
                  await reload();
                }}
                onRequestDate={async (stageId, body) => {
                  await api.requestDateChange(fetcher, task.id, { taskStageId: stageId, ...body });
                  await reload();
                }}
              />
            </Modal>
          ) : null}

          <TaskNotes fetcher={fetcher} taskId={task.id} people={people} />
        </>
      )}

          {/* What happened and who changed what, below the notes and full
              width (punch list #4, 12 Oct 2026). They are read through, not
              glanced at, and the side column made every line wrap three
              times. Why dates moved joined them the same day (Jeremy's
              yes); date requests stay in the side column, being actions. */}
          <TaskHistory
            dateChanges={detail.dateChanges}
            history={detail.history}
            activity={detail.activity}
            currentStageStarted={detail.stages.find((s) => s.isCurrent)?.state !== "not_started"}
          />
        </div>

        <aside className="task-side">
          {/* The task's own custom fields (E3/4). Nothing when it has none. */}
          <SubjectFields
            fetcher={fetcher}
            subject={{ level: "task", id: task.id }}
            title="Task details"
            locked
          />
          <WhoIsOn
            fetcher={fetcher}
            taskId={task.id}
            meKey={me?.ajeraKey ?? null}
            ownerKey={detail.ownerKey}
            ownerName={detail.ownerName}
            assigneeName={detail.task.assigneeName}
            // Never handed to anyone else (F174).
            mayChangeOwner={detail.editability.handOn && !cancelled && !detail.editability.personal}
            // PM-flagged people and admins (F121); the plan is for changing,
            // so a cancelled task's is read on its stages.
            onPlan={detail.editability.planAll && detail.workflowName && !cancelled ? () => setShowPlan(true) : null}
            stages={detail.stages}
            workflowName={detail.workflowName}
            // On the row of the stage the work would go back to (F169), for
            // whoever the server says may move it back.
            moveBackStageId={detail.editability.reverse ? detail.reverseToStageId : null}
            onMoveBack={() => setShowMoveBack(true)}
            busy={busy}
            onChanged={() => void reload()}
          />

          {/* Field work keeps its own card here. The handoff's "RELATED FIELD
              WORK" panel shows a LINKED task's live status, which is Feature
              2 and is not built -- see "Not built" in CLAUDE.md. */}
          {detail.requiresMobilization ? (
            <Mobilization
              fetcher={fetcher}
              task={detail.task}
              mayLink={detail.editability.handOn && !cancelled}
              cancelled={cancelled}
              onChanged={() => void reload()}
            />
          ) : null}

          {detail.dateRequests.length > 0 ? (
            <DateRequests
              requests={detail.dateRequests}
              // Whoever the ask was sent to -- the task's PM, or the
              // project's PM when it has none -- or an admin (E5.3). The
              // server's own answer, so it checks the same rule again.
              mayDecide={detail.editability.decideDateRequests}
              // Rejecting still clears an ask away; approving would move a
              // date on work that is not happening, and is refused.
              mayApprove={!cancelled}
              busy={busy}
              onDecide={(id, approve, note) =>
                void act(() => api.decideDateChange(fetcher, id, approve, note))
              }
            />
          ) : null}


        </aside>
      </div>
    </>
  );
}

/**
 * Why dates moved, the stage route taken and the activity log, under the
 * notes (punch list #4).
 * Each section renders only when it has rows, as it did in the side column.
 */
function TaskHistory({
  dateChanges,
  history,
  activity,
  currentStageStarted,
}: {
  dateChanges: TaskDetailData["dateChanges"];
  history: TaskDetailData["history"];
  activity: TaskDetailData["activity"];
  /**
   * Whether anyone has pressed Start on the stage the work is at. The one
   * open entry in the history IS that stage, so it says "arrived - not
   * started" until then (punch list #42/#50, 20 Oct 2026); it said "in
   * progress" from the moment the work arrived.
   */
  currentStageStarted: boolean;
}) {
  return (
    <>
          {dateChanges.length > 0 ? (
            <section className="task-history">
              <div className="section-label">
                <span>Why dates moved</span>
              </div>
              <ul className="history">
                {dateChanges.map((d, i) => (
                  <li key={i}>
                    <strong>{d.subject}</strong>: {d.previousDate ? mdy(d.previousDate) : "not set"} →{" "}
                    {d.newDate ? mdy(d.newDate) : "cleared"}{" "}
                    {d.reasonCategory ? <span className="chip">{d.reasonCategory}</span> : null}
                    {d.note ? <span className="muted"> {d.note}</span> : null}
                    {/* WHO, which this panel never showed until 7 Oct 2026.
                        The column was written on every date move and could not
                        be resolved to a name, because it held an Entra oid and
                        people resolve by Ajera key.

                        Silent when unknown rather than "unknown": null means
                        either a reporter with no Deltek record or a row older
                        than the backfill, and a row that simply does not claim
                        a person is more honest than one asserting we lost
                        them. */}
                    {d.changedBy ? (
                      <span className="muted"> · {d.changedBy}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {history.length > 0 ? (
            <section className="task-history">
              <div className="section-label">
                <span>What happened</span>
              </div>
              <ul className="history">
                {history.map((h, i) => (
                  <li key={i}>
                    <strong>{h.stageName}</strong>
                    {/* Closed with NO outcome is not "in progress": it is a
                        stage left when the task was cancelled or moved back,
                        where nobody picked a verdict. It read "in progress"
                        until 12 Oct 2026. A marker end (Won, Lost) also has
                        none, and finished the task rather than being left:
                        it read "left" until 22 Oct 2026. */}
                    {h.outcome
                      ? ` — ${h.outcome}`
                      : h.completedOnArrival
                        ? " — finished on arrival"
                        : h.finishedByComplete
                          ? " — completed"
                          : h.completedAt
                          ? " — left"
                          : currentStageStarted
                            ? " — in progress"
                            : " — arrived, not started"}
                    {h.reason ? <span className="muted"> ({h.reason})</span> : null}
                    {h.reversalNote ? (
                      <>
                        {" "}
                        <span className="chip pm">{h.reversedOwnMove ? "move undone" : "moved back by an admin"}</span>
                        <span className="muted"> {h.reversalNote}</span>
                      </>
                    ) : null}
                    {h.advancedByAdmin ? (
                      <>
                        {" "}
                        <span className="chip pm">advanced by admin</span>
                      </>
                    ) : null}
                    {h.advancedIntoUnready ? (
                      <>
                        {" "}
                        <span className="chip pm">next stage not ready</span>
                      </>
                    ) : null}
                    {h.acknowledgedPastDue ? (
                      <>
                        {" "}
                        <span className="chip pm">
                          moved on past its due date ({mdy(h.acknowledgedPastDue)}) — acknowledged
                        </span>
                      </>
                    ) : null}
                    {h.advancedByPm ? (
                      <>
                        {" "}
                        <span className="chip pm">advanced by PM</span>
                      </>
                    ) : null}
                    {/* One note explains whichever flags are set -- they
                        share it (pm_note), so it is shown once. */}
                    {h.pmNote ? <span className="muted"> {h.pmNote}</span> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {activity.length > 0 ? (
            <section className="task-history">
              <div className="section-label">
                <span>Who changed what</span>
              </div>
              <ul className="history">
                {activity.map((a) => (
                  <ActivityRowView key={a.id} row={a} />
                ))}
              </ul>
            </section>
          ) : null}
    </>
  );
}

/**
 * Which deliverable this task belongs to, and changing it.
 *
 * Grouping is set here rather than from the project list because this is
 * where the person has the context to decide — they are looking at the task
 * itself, not scanning a table. The picker stays shut until asked for, since
 * most of the time the answer is already right.
 */
function PartOf({
  fetcher,
  task,
  current,
  mayMove,
  maySetDate,
  onChanged,
}: {
  fetcher: Fetcher;
  task: TaskView;
  current: DeliverableView | null;
  /** May move it between deliverables (`editability.moveDeliverable`). */
  mayMove: boolean;
  /** May set the task's own due date directly (`editability.dueDate`). */
  maySetDate: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState<DeliverableView[] | null>(null);
  // CONTROLLED, not defaultValue (punch list #15, 12 Oct). The options load
  // AFTER the select first renders; with defaultValue it settled on "Not part
  // of a deliverable" -- the only option there yet -- so picking that did not
  // change anything, and a task could never be taken OUT of a deliverable.
  const [choice, setChoice] = useState(current?.id ?? "");
  // Leaving a deliverable needs the task's own due date (#15): offered
  // pre-filled with the client date it was working to.
  const [leaving, setLeaving] = useState(false);
  const [due, setDue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only this project's deliverables are offered. The database refuses a
  // cross-project grouping outright, so offering one would be an error the
  // person could not have known they were making.
  useEffect(() => {
    if (!editing || options !== null) return;
    void api
      .listProjectWork(fetcher, task.projectId)
      .then((r) => setOptions(r.deliverables))
      .catch(() => setOptions([]));
  }, [editing, options, fetcher, task.projectId]);

  const close = (): void => {
    setEditing(false);
    setLeaving(false);
    setChoice(current?.id ?? "");
    setError(null);
  };

  const move = async (id: string, setDateFirst?: string): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      if (setDateFirst) await api.updateTaskDueDate(fetcher, task.id, setDateFirst);
      await api.updateTask(fetcher, task.id, { deliverableId: id || null });
      close();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  // Picking only CHOOSES; Save moves it (punch list #23, 16 Oct). It saved
  // on the pick until then, and with no Save button in sight Jennifer read
  // the control as broken and left without saving.
  const pick = (id: string): void => {
    setChoice(id);
    if (id === "" && current && !task.dueDate) {
      setDue(current.clientDueDate ?? "");
      setLeaving(true);
      return;
    }
    setLeaving(false);
  };
  const changed = choice !== (current?.id ?? "");

  return (
    <div style={{ marginTop: 6 }}>
      {error ? <div className="error">{error}</div> : null}

      {editing ? (
        <>
          <div className="row part-of-row">
            <select value={choice} disabled={busy || options === null} onChange={(e) => pick(e.target.value)}>
              <option value="">Not part of a deliverable</option>
              {(options ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
            {leaving ? null : (
              <button className="primary" disabled={busy || !changed} onClick={() => void move(choice)}>
                Save
              </button>
            )}
            <button onClick={close} disabled={busy}>
              Cancel
            </button>
          </div>
          {leaving ? (
            maySetDate ? (
              <div className="leave-deliverable">
                <p className="warn" style={{ margin: "8px 0" }}>
                  Outside <strong>{current?.name}</strong> this task has no client date to work to.
                  Give it its own due date to take it out.
                </p>
                <div className="row part-of-row">
                  <input
                    type="date"
                    {...PAST_DATE_BOX}
                    aria-label="The task's own due date"
                    value={due}
                    onChange={(e) => setDue(e.target.value)}
                  />
                  <button className="primary" disabled={busy || due === ""} onClick={() => void move("", due)}>
                    Set the date and take it out
                  </button>
                </div>
              </div>
            ) : (
              <p className="warn" style={{ margin: "8px 0" }}>
                This task has no due date of its own, and leaving <strong>{current?.name}</strong> takes
                away the client date it was working to. Only its PM / Task Manager, its project's PM or TM or an admin sets that
                date - ask them first.
              </p>
            )
          ) : null}
        </>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          {current ? (
            <>
              Part of{" "}
              <button
                className="linkish deliv-name"
                onClick={() => navigate({ kind: "deliverable", id: current.id })}
              >
                {current.name}
              </button>
              {current.clientDueDate
                ? ` · due to the client ${mdy(current.clientDueDate)}`
                : ""}
            </>
          ) : (
            "Not part of a deliverable"
          )}{" "}
          {/* Offered only to the people the server lets move it, like the
              rename beside the name above. */}
          {mayMove ? (
            <button
              className="linkish"
              onClick={() => {
                setChoice(current?.id ?? "");
                setEditing(true);
              }}
            >
              Change
            </button>
          ) : null}
        </p>
      )}
    </div>
  );
}

/**
 * An audit row as a sentence.
 *
 * Written as prose rather than a from/to table because the log is read when
 * somebody is surprised by something, and "Jacob Weiss changed assigned to on
 * Peer review from BETA TEST to Jacob Weiss" answers the question faster than
 * a grid they have to decode.
 */
function describeActivity(a: ActivityRow): string {
  // Only stage-level entries name their subject. A task-level one would
  // otherwise repeat the title of the task already on screen.
  const stage = a.stageName ?? "";

  switch (a.action) {
    case "created":
      return "created this task";
    case "deleted":
      return `deleted ${a.subject}`;
    case "started":
      return `started ${stage}`;
    case "start undone":
      return `undid the start of ${stage}`;
    // Requested work (F140). A rejection is recorded as the cancel it is.
    case "request approved":
      return "approved this request";
    case "blocked":
      return `blocked ${stage}`;
    case "unblocked":
      return `unblocked ${stage}`;
    case "hibernated":
      return `hibernated ${stage}`;
    case "reversed":
      return `moved this back to ${stage}`;
    case "completed":
      return a.newValue ? `completed ${stage} — ${a.newValue}` : `completed ${stage}`;
    // Fell to the edit sentence below until 22 Oct 2026 and read "set
    // something to …", which the resource scheduler's unlink made visible.
    case "linked to deployment":
      return a.newValue ? `linked this to the deployment ${a.newValue}` : "linked this to a deployment";
    case "unlinked from deployment":
      return a.newValue ? `unlinked this from its deployment: ${a.newValue}` : "unlinked this from its deployment";
    default: {
      // An edit. Long values are cut: this is a timeline, not a diff viewer,
      // and a pasted paragraph would push everything else off the screen.
      const from = shownValue(a.previousValue?.trim());
      const to = shownValue(a.newValue?.trim());
      const label = a.field ? `the ${fieldLabel(a.field)}` : "something";
      const where = stage ? ` on ${stage}` : "";
      if (!from && to) return `set ${label}${where} to “${clip(to)}”`;
      if (from && !to) return `cleared ${label}${where}`;
      if (from && to)
        return `changed ${label}${where} from “${clip(from)}” to “${clip(to)}”`;
      return `changed ${label}${where}`;
    }
  }
}

/** A logged value that is a calendar date, as every date reads (F193). */
function shownValue(value: string | undefined): string | undefined {
  return value !== undefined && /^\d{4}-\d{2}-\d{2}$/.test(value) ? mdy(value) : value;
}

function clip(value: string, max = 60): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

/** "3 minutes ago" beats a timestamp nobody converts in their head. */
function whenLabel(iso: string): string {
  const then = new Date(iso).getTime();
  const mins = Math.round((Date.now() - then) / 60000);

  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;

  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;

  // Past a month the exact date is more use than a vague count.
  return mdyDay(iso);
}

/**
 * The selected stage, as a tab panel.
 *
 * Always open — the tabs decide what you are looking at, so there is nothing to
 * expand. The actions sit at the bottom, under the thing they apply to, and
 * only appear on the stage the work is actually on.
 */
function Stage({
  stage,
  outcomes,
  people,
  clientDueDate,
  deliverableName,
  requiresHours,
  requiresDates,
  listing,
  onAddItem,
  onRemoveItem,
  onSetItem,
  onNumberItem,
  busy,
  isEditing,
  onToggleEdit,
  onSave,
  dateAuthority,
  fromBooking,
  mayAssignOthers,
  mayChangeWork,
  mayChangeInstructions,
  onRequestDate,
  onStart,
  onUndoStart,
  onBlock,
  loadBlockTargets,
  loadBlockOptions,
  onSetBlockTarget,
  onComplete,
  advance,
  mayHibernate,
  cancelled,
  overdueDays,
  onAddMe,
  onReadAll,
  error,
}: {
  stage: TaskStageView;
  /** Open every stage's instructions in a pop-up (punch list #5). */
  onReadAll: () => void;
  outcomes: readonly StageOutcome[];
  people: ReferenceOption[];
  clientDueDate?: string | null;
  deliverableName?: string | null;
  /** The workflow requires hours on staffed stages (E7.3). */
  requiresHours: boolean;
  requiresDates: boolean;
  /** This stage's object listing (E1), or null when it carries none. */
  listing: StageListingData | null;
  onAddItem: (input: { typeId?: string; name?: string }) => void;
  onRemoveItem: (itemId: string) => void;
  onNumberItem: (itemId: string, number: number) => void;
  onSetItem: (
    itemId: string,
    input: { done: boolean; filePath?: string | null; note?: string | null },
  ) => void;
  busy: boolean;
  isEditing: boolean;
  onToggleEdit: () => void;
  onSave: (body: {
    description?: string | null;
    dueDate?: string | null;
    assigneeKeys?: string[];
  }) => void;
  /** This person's authority over the stage's date (E5.2), from `editability`. */
  dateAuthority: DateAuthority;
  /**
   * Its date, crew and hours come from the task's resource-scheduler booking and are changed
   * there (`editability.schedulerOwnedStages`, 21 Oct 2026).
   */
  fromBooking: boolean;
  /** May put somebody else on the stage (`editability.stageAssignOthers`, 22 and 29 Oct 2026). */
  mayAssignOthers: boolean;
  /**
   * May change its hours: somebody on it, the task's PM / Task Manager or an
   * admin (`editability.stageWork`, F143).
   */
  mayChangeWork: boolean;
  /**
   * May change its instructions and tick its checklist: anybody linked to
   * Deltek, except in a personal or the template project
   * (`editability.stageInstructions`, Jeremy, 3 Nov 2026).
   */
  mayChangeInstructions: boolean;
  onRequestDate: (body: {
    date: string | null;
    reasonCategory?: DateChangeReason;
    note?: string | null;
  }) => void;
  /**
   * Start, and Undo start while the stage is still In progress: for whoever
   * may move it on (`advance.right`, F139/F142, 30 Oct 2026) -- somebody on
   * it, the task's PM / Task Manager or an admin. The server asks the same.
   */
  onStart: () => void;
  onUndoStart: () => void;
  onBlock: (blocked: boolean, req?: BlockRequest) => void;
  /**
   * What this task may wait on -- STEP 2 of the dependency rule.
   *
   * A CALLBACK, not the api client and a fetcher. Every other operation this
   * component performs is a prop, and keeping that true is what lets it be
   * rendered in a harness without standing up authentication.
   */
  loadBlockTargets: () => Promise<readonly BlockTargetOption[]>;
  /** Reasons and snooze position for the Block dialog (hold rows 1b, 6b). */
  loadBlockOptions: () => Promise<BlockOptions>;
  /**
   * Point the current block at a stage, re-point it, or unlink it with null.
   * Resolves TRUE only when it saved, so the form closes on success and stays
   * open -- with what the person chose -- when it did not.
   */
  onSetBlockTarget: (waitsOnStageId: string | null) => Promise<boolean>;
  onComplete: (
    outcome?: string,
    reason?: string,
    pmNote?: string,
    overrideUnready?: boolean,
    /** Warned that the next stage's date has passed, and moving on (E11.5). */
    acknowledgePastDue?: boolean,
  ) => void;
  advance: AdvancePermission;
  /** Whether this person may hibernate here (hold item 3b). */
  mayHibernate: boolean;
  /**
   * The task is cancelled: shown as it stood, with nothing to start, move,
   * block, edit or tick until it is reinstated -- the server refuses all of it.
   */
  cancelled: boolean;
  /** Days the current stage's date is behind, or null: shown beside it (F150). */
  overdueDays: number | null;
  onAddMe: () => void;
  /** Shown here as well as at the top: a refusal you cannot see reads as
      nothing having happened, and the actions are far from the page head. */
  error?: string | null;
}) {
  // Held here rather than inside StageActions so the prompts can render at
  // full width in the body while the buttons stay in the header.
  // ONE prompt for whatever an outcome needs, since 12 Oct: a reason when it
  // sends work back, a note when somebody steps in for the holder (owner or
  // admin), and the override when the next stage is not ready (E7.9). Until
  // then these were two prompts, and an owner sending work BACK on a stage
  // they did not hold was asked only for the reason -- the server, wanting
  // the note too, refused.
  const [asking, setAsking] = useState<StageOutcome | null>(null);
  const [reason, setReason] = useState("");
  const [pmNote, setPmNote] = useState("");
  // A block REQUIRES a reason -- it is what somebody reads three weeks later to
  // know whether the condition still holds. Same prompt shape as a send-back.
  const [blocking, setBlocking] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  // Whether this block also hibernates the work. Reset on both exits
  // from the prompt, so the next block starts from the ordinary case
  // rather than inheriting a decision made about a different hold.
  const [blockHibernates, setBlockHibernates] = useState(false);
  // Which of the three release conditions this block carries. `manual` is the
  // default and is every block written before 10 Oct 2026.
  const [releaseKind, setReleaseKind] = useState<BlockReleaseKind>("manual");
  const [waitsOnTaskId, setWaitsOnTaskId] = useState<string | null>(null);
  const [waitsOnStageId, setWaitsOnStageId] = useState<string | null>(null);
  const [snoozeUntil, setSnoozeUntil] = useState("");
  const [targets, setTargets] = useState<readonly BlockTargetOption[] | null>(null);
  const [targetsFailed, setTargetsFailed] = useState(false);
  // The reasons this stage offers and where its snoozes stand (hold rows 1b,
  // 6b), fetched as the dialog opens -- the answers the block write enforces
  // with. Null when they could not be had: the dialog then falls back to the
  // free-text reason, which still works.
  const [options, setOptions] = useState<BlockOptions | null>(null);
  const [reasonKey, setReasonKey] = useState("");
  // Asked ONCE per opening, and the answer kept only for that opening.
  // `loadBlockOptions` is a fresh arrow on every parent render, so an effect
  // cleanup would cancel the request the first time the page re-rendered;
  // the opening's number is what tells a late answer it is stale.
  const optionsOpening = useRef<number | null>(null);
  const openings = useRef(0);
  // The Link it control: choosing what an EXISTING block waits on. Its own
  // state rather than the dialog's, so opening one never pre-fills the other.
  const [linking, setLinking] = useState(false);
  const [linkTaskId, setLinkTaskId] = useState<string | null>(null);
  const [linkStageId, setLinkStageId] = useState<string | null>(null);

  /** Put the dialog back to the ordinary case, whichever way it was left. */
  const resetBlockPrompt = useCallback(() => {
    setBlocking(false);
    setReasonKey("");
    setOptions(null);
    optionsOpening.current = null;
    setBlockHibernates(false);
    setReleaseKind("manual");
    setWaitsOnTaskId(null);
    setWaitsOnStageId(null);
    setSnoozeUntil("");
  }, []);

  // Fetched when the dialog OPENS, not with the task: it is only ever needed
  // here and it grows with the project, so putting it on the task read would
  // make the commonest page pay for the rarest action.
  //
  // A failure is NOT fatal to the dialog. Blocking with a plain reason is the
  // thing people did yesterday and must keep working if this list cannot be
  // had -- so the condition modes say they are unavailable and the rest of
  // the prompt carries on, rather than the dialog erroring.
  useEffect(() => {
    if (!blocking || optionsOpening.current !== null) return;
    const opening = (openings.current += 1);
    optionsOpening.current = opening;
    void loadBlockOptions()
      .then((o) => optionsOpening.current === opening && setOptions(o))
      .catch(() => undefined);
  }, [blocking, loadBlockOptions]);

  useEffect(() => {
    if (!(blocking || linking) || targets !== null) return;
    let live = true;
    void loadBlockTargets()
      .then((list) => live && setTargets(list))
      .catch(() => live && setTargetsFailed(true));
    return () => {
      live = false;
    };
  }, [blocking, linking, targets, loadBlockTargets]);

  /** Open the Link it control on what the block points at now, if anything. */
  const openLinking = () => {
    setLinkTaskId(stage.block?.waitsOn?.taskId ?? null);
    setLinkStageId(stage.block?.waitsOn?.stageId ?? null);
    setLinking(true);
  };
  const currentTarget = stage.block?.waitsOn?.stageId ?? null;
  // Nothing to save when the choice is what is already there, or when a task
  // is chosen with no stage -- "this task" is not a target, a stage of it is.
  const linkUnchanged = linkStageId === currentTarget;
  const linkIncomplete = linkTaskId !== null && linkStageId === null;
  // Where the work is AND still to be done. A finished task stays on its end
  // stage, complete, so `isCurrent` alone offered Complete on a proposal
  // already Won -- refused as "already complete" -- and told an admin they
  // could move it on (22 Oct 2026).
  // And not on a cancelled task, which nobody may move until it is reinstated.
  const actionable = stage.isCurrent && stage.state !== "complete" && !cancelled;

  return (
    <div className="stage-panel">
      {/* Who and when on the left, what you can DO on the right. The actions
          are the reason most people open this page, so they sit where the eye
          lands rather than below the instructions. */}
      <div className="row spread stage-head">
        <div>
          {/* The name, then what you do to the stage itself: edit its
              instructions, and start it (punch list #7, #8, 12 Oct 2026).
              Both sat away from the name -- Edit under the instructions,
              Start among the outcome buttons on the right. */}
          <div className="stage-title-row">
            <h3>{stage.name}</h3>
            {!isEditing && !cancelled ? (
              <EditChip title="Edit the instructions for this stage" onClick={onToggleEdit} />
            ) : null}
            {/* Only for whoever may move the stage on (F139/F142, 30 Oct
                2026); until then anybody could start any task. */}
            {!isEditing && !cancelled && stage.isCurrent && stage.state === "not_started" && advance.right !== "none" ? (
              <button className="primary stage-start" onClick={onStart} disabled={busy}>
                Start
              </button>
            ) : null}
            {!isEditing && !cancelled && stage.isCurrent && stage.state === "in_progress" && advance.right !== "none" ? (
              <button className="stage-undo-start" onClick={onUndoStart} disabled={busy} title="Back to Not started">
                Undo start
              </button>
            ) : null}
          </div>
          <div className="stage-meta">
            {stage.assignees.length > 0
              ? stage.assignees.map((a) => a.label).join(", ")
              : "Unassigned"}
            {stage.dueDate ? ` · due ${mdy(stage.dueDate)}` : " · no date"}
            <OverdueMark days={overdueDays} />
            {stage.allocatedHours === null
              ? ""
              : stage.allocatedHours === 0
                ? " · 0 h (deliberately no time)"
                : ` · ${stage.allocatedHours} h allocated`}
          </div>
        </div>
        <div className="row stage-actions-top">
          <span className={`state ${stage.state}`}>{stage.state.replace("_", " ")}</span>
          {!isEditing && actionable ? (
            <StageActions
              stage={stage}
              outcomes={outcomes}
              busy={busy}
              advance={advance}
              onBlock={onBlock}
              onComplete={onComplete}
              onAsk={setAsking}
              onAskBlock={() => setBlocking(true)}
            />
          ) : null}
        </div>
      </div>

      {isEditing ? (
        <StageEditor
          stage={stage}
          people={people}
          clientDueDate={clientDueDate}
          deliverableName={deliverableName}
          requiresHours={requiresHours}
          requiresDates={requiresDates}
          busy={busy}
          error={error}
          dateAuthority={dateAuthority}
          fromBooking={fromBooking}
          mayAssignOthers={mayAssignOthers}
          mayChangeWork={mayChangeWork}
          mayChangeInstructions={mayChangeInstructions}
          onRequestDate={onRequestDate}
          onCancel={onToggleEdit}
          onSave={onSave}
        />
      ) : (
        <>
          {/* WHY IT IS HELD, above the instructions rather than below them.
              A block is a condition governing the whole stage, so reading the
              instructions first and discovering afterwards that none of it can
              be finished is the wrong order.

              Until 10 Oct 2026 this was nowhere on the screen at all. The
              reason is required by a CHECK constraint — "it is what somebody
              reads three weeks later to decide whether the condition still
              holds" — and the browser had never once shown it. A required
              field nobody can read back is how you teach people to type "x". */}
          {stage.block ? (
            <div className={stage.block.hibernatedAt ? "stage-block hibernated" : "stage-block"}>
              <span className="stage-block-tag">
                {stage.block.hibernatedAt ? "HIBERNATED" : "BLOCKED"}
              </span>
              <div>
                <p className="stage-block-reason">
                  {stage.block.reasonLabel ? <strong>{stage.block.reasonLabel}</strong> : null}
                  {stage.block.reasonLabel && stage.block.reason ? " — " : null}
                  {stage.block.reason}
                </p>

                {/* THE RECORDED CONDITION, ahead of the typed sentence.

                    `releaseKind` is read rather than testing `waitsOn` for
                    null, because null with kind `stage` is UNRESOLVED -- a
                    true and deliberate state (E2.15) -- and testing the
                    target alone reports it as an ordinary manual block. That
                    is the same mistake as `undefined !== null`: a plausible,
                    specific, wrong answer. */}
                {stage.block.releaseKind === "stage" ? (
                  <p className="stage-block-until">
                    {stage.block.waitsOn ? (
                      <>
                        Until <strong>{stage.block.waitsOn.taskTitle}</strong> reaches{" "}
                        <strong>{stage.block.waitsOn.stageName}</strong>
                      </>
                    ) : (
                      "Waiting on another task, not yet linked"
                    )}
                    {/* THE EXIT FROM AN UNRESOLVED BLOCK -- hold build list
                        item 7. Until 11 Oct an unresolved block could be
                        created and never resolved: the only statements
                        touching an open block both RELEASE it.

                        CHANGE as well as LINK IT, because decision 9's case
                        is "this one is pointed at the wrong mobilization".
                        Current stage only, because that is the only block
                        the setter can reach. */}
                    {stage.isCurrent && !linking && !cancelled ? (
                      <EditChip
                        label={stage.block.waitsOn ? "CHANGE" : "LINK IT"}
                        title={
                          stage.block.waitsOn
                            ? "Change which task and stage this is waiting on"
                            : "Choose the task and stage this is waiting on"
                        }
                        onClick={openLinking}
                      />
                    ) : null}
                  </p>
                ) : null}

                {linking && stage.block.releaseKind === "stage" ? (
                  <div className="block-link">
                    {/* A failed load is said as one, never as an empty list:
                        "nothing can be waited on" would be a plausible,
                        specific, wrong answer. */}
                    {targetsFailed ? (
                      <p className="muted">
                        Could not load what this task could wait on. Try again
                        in a moment.
                      </p>
                    ) : (
                      <BlockTargetPicker
                        idPrefix={`link-${stage.id}`}
                        targets={targets}
                        taskId={linkTaskId}
                        stageId={linkStageId}
                        onTask={setLinkTaskId}
                        onStage={setLinkStageId}
                        emptyHint="It stays blocked until somebody releases it."
                      />
                    )}
                    {/* Unlinking is NOT releasing, and the button says
                        which it is doing so nobody unlinks expecting the
                        work to become advanceable. */}
                    <div className="row block-link-actions">
                      <button
                        type="button"
                        className="primary"
                        disabled={busy || linkUnchanged || linkIncomplete}
                        onClick={() =>
                          void onSetBlockTarget(linkStageId).then(
                            (saved) => saved && setLinking(false),
                          )
                        }
                      >
                        {linkStageId === null && currentTarget !== null
                          ? "Unlink — stays blocked"
                          : "Link it"}
                      </button>
                      <button
                        type="button"
                        className="secondary"
                        disabled={busy}
                        onClick={() => setLinking(false)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : null}

                {stage.block.releaseKind === "date" && stage.block.snoozeUntil ? (
                  <p className="stage-block-until">
                    Checking back on <strong>{mdy(stage.block.snoozeUntil)}</strong>
                  </p>
                ) : null}

                {/* The human sentence stays, and sits BELOW the recorded
                    condition when there is one. For a manual block it is the
                    only thing there is; for the others it is the "why" behind
                    a "what". */}
                {stage.block.releaseCondition ? (
                  <p className="stage-block-until">
                    {stage.block.releaseKind === "manual" ? "Until: " : ""}
                    {stage.block.releaseCondition}
                  </p>
                ) : null}
                <p className="stage-block-when">
                  {stage.block.hibernatedAt
                    ? /* Both dates, because they answer different questions:
                         when did this stop moving, and when did we decide to
                         stop looking at it. They are usually the same moment
                         and will not be once the sweep runs. */
                      `Blocked ${whenLabel(stage.block.blockedAt)} · hibernated since ${whenLabel(stage.block.hibernatedAt)}`
                    : `Blocked ${whenLabel(stage.block.blockedAt)}`}
                </p>
              </div>
            </div>
          ) : null}

          {/* Why the work came back, directly ABOVE the instructions (F175,
              1 Nov 2026; under them until then) rather than in the history.
              Somebody opening "Author edits" needs the review comments in
              front of them; buried in a list of everything
              that ever happened, they get skipped. */}
          {stage.sentBackReason ? (
            <div className="sent-back">
              <span className="sent-back-tag">
                Sent back
                {stage.sentBackFrom ? ` from ${stage.sentBackFrom}` : ""}
              </span>
              <div className="sent-back-body">{stage.sentBackReason}</div>
            </div>
          ) : null}

          {/* ABOVE the instructions, not below them (punch list F175, Jeremy
              1 Nov 2026): "it is really hard to see these if the instructions
              are very long." A refusal, the rules about moving the stage on
              and the reason/note prompt all answer something pressed in the
              header, so they sit next to it. */}
          {error ? <div className="error">{error}</div> : null}

          {/* Said before anyone presses anything, not after. A refusal that
              only appears once you have tried reads as the app breaking;
              stated up front with the way out, it reads as a rule. */}
          {actionable && advance.right === "none" && fromBooking ? (
            <div className="warn" style={{ marginTop: 12 }}>
              <strong>This stage is not yours to move on.</strong> It is held
              by the crew of the task's resource-scheduler booking. To be on it,
              be added to the crew in the scheduler.
            </div>
          ) : actionable && advance.right === "none" ? (
            <div className="warn" style={{ marginTop: 12 }}>
              <strong>This stage is not yours to move on.</strong> It is
              assigned to{" "}
              {stage.assignees.length > 0
                ? stage.assignees.map((a) => a.label).join(", ")
                : "nobody"}
              , and you are not its PM / Task Manager or its project's PM or TM. Add yourself to the stage and you
              can carry on — it will show that you did.
              <div style={{ marginTop: 8 }}>
                <button onClick={onAddMe} disabled={busy}>
                  Add me to this stage
                </button>
              </div>
            </div>
          ) : null}

          {/* Said before anybody presses, like the rule above: an outcome
              leading into a stage that is not ready is inert for somebody who
              may not move it on anyway, and this says why and who can. */}
          {actionable && !advance.mayOverrideUnready
            ? outcomes
                .filter((o) => o.unready !== null)
                .map((o) => (
                  <p key={o.outcome} className="warn" style={{ marginTop: 10 }}>
                    <strong>{o.toStageName}</strong> has {o.unready}. {unreadyFix(o.unready!)}, or
                    ask the task's PM / Task Manager, its project's PM or TM or an admin to move it on anyway.
                  </p>
                ))
            : null}

          {actionable && advance.right === "owner_override" ? (
            <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
              You are the PM / Task Manager for this task (or its project's PM or TM) but are not assigned to
              this stage. Moving it on will be recorded as advanced by the PM,
              and asks for a short note.
            </p>
          ) : null}

          {actionable && advance.right === "admin_override" ? (
            <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
              This stage is not assigned to you and you are not the task's PM /
              Task Manager or its project's PM or TM. As an admin you can move it on; it will be recorded
              as advanced by an admin, and asks for a short note.
            </p>
          ) : null}

          {/* Full width, because this is the most considered thing anyone
              writes on the page and it decides what the next person does. */}
          {asking ? (
            <AdvancePrompt
              stageId={stage.id}
              outcome={asking}
              advance={advance}
              reason={reason}
              note={pmNote}
              busy={busy}
              onReason={setReason}
              onNote={setPmNote}
              onCancel={() => {
                setAsking(null);
                setReason("");
                setPmNote("");
              }}
              onConfirm={() => {
                onComplete(
                  asking.outcome === COMPLETE ? undefined : asking.outcome,
                  asking.isRework ? reason.trim() : undefined,
                  pmNote.trim() || undefined,
                  asking.unready !== null,
                  asking.pastDue !== null,
                );
                setAsking(null);
                setReason("");
                setPmNote("");
              }}
            />
          ) : null}

          {/* The instructions are the reason this model exists at all — the
              thing people were making a subtask per stage to get somewhere to
              write. Given a box of its own, and room to be read. */}
          {/* Directly above the instructions it extends (punch list #5). */}
          <div className="instructions-bar">
            <button type="button" className="linkish" onClick={onReadAll}>
              Read all instructions
            </button>
          </div>
          <div className="instructions">
            {stage.description ? (
              <RichText
                text={stage.description}
                // Ticking a box saves immediately. A checklist you have to
                // remember to save is one people stop trusting, and the
                // change is a single character in a field that is already
                // audited.
                // None on a cancelled task, nor for somebody who may not
                // change the instructions (`stageInstructions`): the boxes
                // render, disabled, as in the all-instructions sheet.
                onToggleCheck={
                  cancelled || !mayChangeInstructions
                    ? undefined
                    : (line) => onSave({ description: toggleCheckAtLine(stage.description ?? "", line) })
                }
              />
            ) : (
              <span className="muted">No instructions for this stage yet.</span>
            )}
          </div>

          {/* What this stage PRODUCES (E1) -- under the instructions that say
              how, and on every stage that carries a list, current or not:
              items are chosen before the work arrives. */}
          {listing ? (
            <StageListing
              listing={listing}
              stageName={stage.name}
              busy={busy}
              onAdd={onAddItem}
              onRemove={onRemoveItem}
              onSet={onSetItem}
              onNumber={onNumberItem}
            />
          ) : null}


          {/* A DIALOG, not another section of the page. Until 10 Oct 2026 this
              rendered at the bottom of the panel, below the instructions --
              Jeremy: "when you hit the block button the reason box pops up not
              just appears at the bottom, it is not intuitive to have it there."

              The reason it was wrong is what the thing IS. This is a question
              that must be answered before anything else happens, and rendering
              it as more content says the opposite. On a stage with long
              instructions it opened below the fold, so pressing Block looked
              like it had done nothing.

              The rework reason and the PM note prompts have the same shape;
              since F175 (1 Nov 2026) they sit above the instructions instead,
              next to the buttons that open them. */}
          {blocking ? (
            <Modal
              title="Block this stage"
              onCancel={resetBlockPrompt}
            >
              {/* A NAMED reason first (hold decision 1), so "how much is held
                  on clients?" has an answer; the detail beneath is optional
                  with one, and required for Other. With no list (it failed
                  to load) the detail is the reason, as before. */}
              {options && options.reasons.length > 0 ? (
                <div className="field">
                  <label htmlFor={`block-why-${stage.id}`}>Why is it held?</label>
                  <select
                    id={`block-why-${stage.id}`}
                    value={reasonKey}
                    onChange={(e) => setReasonKey(e.target.value)}
                    autoFocus
                  >
                    <option value="">Choose a reason…</option>
                    {options.reasons.map((r) => (
                      <option key={r.key} value={r.key}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}
              <div className="field">
                <label htmlFor={`block-${stage.id}`}>
                  {options && options.reasons.length > 0
                    ? reasonKey === "OTHER"
                      ? "What is it waiting on?"
                      : "Detail — optional"
                    : "Why is this blocked?"}
                </label>
                <textarea
                  id={`block-${stage.id}`}
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="What it is waiting on, and what would release it"
                  autoFocus
                />
              </div>

              {/* WHAT WOULD RELEASE IT — the three conditions, as radios.

                  Radios rather than a dropdown because the choice changes what
                  the rest of the dialog asks for, and a closed select hides
                  two of three options behind a click on the control that
                  decides the shape of the form.

                  `manual` is first and is the default: it is what every block
                  before 10 Oct 2026 was, and the thing most people want. */}
              <fieldset className="block-release">
                <legend>What releases it?</legend>

                <label>
                  <input
                    type="radio"
                    name={`release-${stage.id}`}
                    checked={releaseKind === "manual"}
                    onChange={() => setReleaseKind("manual")}
                  />
                  <span>
                    <strong>Someone releases it.</strong> Anybody can, whenever
                    the hold is over.
                  </span>
                </label>

                <label>
                  <input
                    type="radio"
                    name={`release-${stage.id}`}
                    checked={releaseKind === "stage"}
                    disabled={targetsFailed}
                    onChange={() => setReleaseKind("stage")}
                  />
                  <span>
                    <strong>When another task reaches a stage.</strong> A named
                    stage, not the whole task — a report can often start once
                    field work reaches Mobilize.
                  </span>
                </label>

                {releaseKind === "stage" ? (
                  <div className="block-target">
                    <BlockTargetPicker
                      idPrefix={`waits-${stage.id}`}
                      targets={targets}
                      taskId={waitsOnTaskId}
                      stageId={waitsOnStageId}
                      onTask={setWaitsOnTaskId}
                      onStage={setWaitsOnStageId}
                      emptyHint="You can still block it and say why."
                    />
                    {/* UNRESOLVED IS LEGAL AND MEANT — decision E2.15.
                        "Draft is blocked, waiting on field work (not yet
                        linked)" is a TRUE statement and it still stops the
                        report being drafted onward. Saying so here is what
                        stops somebody choosing the wrong task to make the
                        dialog let them through. */}
                    {targets && targets.length > 0 && !waitsOnStageId ? (
                      <p className="muted">
                        You can leave this unset. The stage stays blocked and
                        says it is waiting on something not yet linked.
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <label>
                  <input
                    type="radio"
                    name={`release-${stage.id}`}
                    checked={releaseKind === "date"}
                    // Spent: hibernating (or a block with no date) is the only
                    // way to hold it longer -- the rule the server enforces.
                    disabled={options?.snooze.kind === "used_up"}
                    onChange={() => setReleaseKind("date")}
                  />
                  <span>
                    <strong>Check back on a date.</strong> For a hold with no
                    event to wait for — chasing an agency, or a client who has
                    gone quiet.
                  </span>
                </label>

                {releaseKind === "date" ? (
                  <div className="block-target">
                    <div className="field">
                      <label htmlFor={`snooze-${stage.id}`}>Check back on</label>
                      <input
                        id={`snooze-${stage.id}`}
                        type="date"
                        {...PAST_DATE_BOX}
                        value={snoozeUntil}
                        min={options?.snooze.kind === "matrix" ? (options.snooze.earliest ?? undefined) : undefined}
                        max={
                          options && options.snooze.kind !== "used_up"
                            ? (options.snooze.latest ?? undefined)
                            : undefined
                        }
                        onChange={(e) => setSnoozeUntil(e.target.value)}
                      />
                    </div>
                    {options ? <SnoozeLine position={options.snooze} /> : null}
                  </div>
                ) : null}

                {options?.snooze.kind === "used_up" ? (
                  <p className="muted">
                    This stage has had its {options.snooze.of} snooze
                    {options.snooze.of === 1 ? "" : "s"}. To hold it longer, hibernate it — or
                    block it with no date.
                  </p>
                ) : null}

                {targetsFailed ? (
                  /* DEGRADE, never fail. Blocking with a plain reason is what
                     people did yesterday and must keep working when this list
                     cannot be had. The sentence says the option is missing, so
                     nobody concludes the feature is broken. */
                  <p className="muted">
                    Could not load what this task could wait on, so that option
                    is unavailable. Blocking with a reason still works.
                  </p>
                ) : null}
              </fieldset>

              {/* ONE PROMPT, TWO ENDINGS, and not two buttons in the header.
                  Both need the same required reason, and the choice between
                  them is a judgement about this particular hold — "is anybody
                  chasing this?" — which is the question you are already
                  answering while you type the reason.

                  Unticked is the default and means an ordinary block: the
                  Blocked count means "someone should act", and that is the
                  only thing it is for. Ticking it is how a report the client
                  has gone quiet on stops diluting that count.

                  THE WORD IS "HIBERNATE", on screen as well as in the
                  database and the docs. It read "sleep" here first;
                  Jeremy's call, 10 Oct 2026. The reason is that the admin
                  screen shows a setting called
                  `hibernation.after_business_days` and training runs
                  2-6 Nov -- one concept wearing two words is how a bug
                  report and a document stop being about the same thing. */}
              {/* Offered only to somebody the stage lets hibernate (hold
                  item 3b) -- the same answer the server enforces. Everybody
                  else blocks; a block is a door, hibernating is not. */}
              {mayHibernate ? (
              <label className="block-hibernate">
                <input
                  type="checkbox"
                  checked={blockHibernates}
                  onChange={(e) => setBlockHibernates(e.target.checked)}
                />
                <span>
                  <strong>Also hibernate it.</strong> It leaves to-do
                  lists and workload, and stops holding its project open — but
                  it is still here, still unfinished, and still counted at its
                  real status. For work nobody is chasing.
                </span>
              </label>
              ) : null}

              <div className="row" style={{ justifyContent: "flex-end" }}>
                <button onClick={resetBlockPrompt} disabled={busy}>
                  Cancel
                </button>
                <button
                  className="primary"
                  /* A snooze with no date is the one incomplete combination
                     the dialog can produce. The server refuses it too -- "Say
                     what date to check back on." -- but a disabled button is
                     a better answer than a round trip to be told no.

                     A STAGE with no target is NOT incomplete. It is
                     UNRESOLVED, which is a state this design wants. */
                  disabled={
                    busy ||
                    (options && options.reasons.length > 0
                      ? reasonKey === "" || (reasonKey === "OTHER" && blockReason.trim() === "")
                      : blockReason.trim() === "") ||
                    (releaseKind === "date" && snoozeUntil === "")
                  }
                  onClick={() => {
                    onBlock(true, {
                      reason: blockReason,
                      reasonKey: reasonKey || null,
                      hibernate: mayHibernate && blockHibernates,
                      releaseKind,
                      // Sent as null unless the kind asks for it. The CHECK
                      // constraint refuses a row holding a value its kind does
                      // not own, so leaving a stale one here from a mode the
                      // user moved away from would be a 400 they could not
                      // see the cause of.
                      waitsOnStageId: releaseKind === "stage" ? waitsOnStageId : null,
                      snoozeUntil: releaseKind === "date" ? snoozeUntil : null,
                    });
                    resetBlockPrompt();
                    setBlockReason("");
                  }}
                >
                  {/* The button says which of the two is about to happen.
                      A fixed "Block" beside a ticked box makes somebody
                      check the box again before pressing it. */}
                  {mayHibernate && blockHibernates ? "Block and hibernate" : "Block"}
                </button>
              </div>
            </Modal>
          ) : null}

          {!stage.isCurrent ? (
            <p className="muted" style={{ marginBottom: 0, marginTop: 10 }}>
              The work is not on this stage right now.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}

/**
 * The buttons only.
 *
 * The rework prompt used to live in here, which put a textarea inside the
 * narrow column the actions sit in — a box two inches wide for the most
 * considered thing anyone writes on this page. It is now raised to the panel
 * body, where it gets the full width.
 */
/*
 * `stageStateLabel` was removed on 4 Oct 2026 with the tab cards it wrote
 * labels for. The strip says the state with COLOUR now, and the one stage
 * whose state needs words -- the current one -- has the whole panel below it,
 * which already carries the late notice.
 */

function StageActions({
  stage,
  outcomes,
  busy,
  onBlock,
  onComplete,
  onAsk,
  advance,
  onAskBlock,
}: {
  stage: TaskStageView;
  outcomes: readonly StageOutcome[];
  busy: boolean;
  onBlock: (blocked: boolean, req?: BlockRequest) => void;
  onAskBlock: () => void;
  onComplete: (outcome?: string) => void;
  /** Open the prompt for an outcome that needs a reason, a note or an override. */
  onAsk: (outcome: StageOutcome) => void;
  advance: AdvancePermission;
}) {
  // Refused people keep the buttons visible but inert. Hiding them would
  // leave somebody wondering where the workflow went; disabled beside an
  // explanation says "not you", which is the true answer.
  const refused = advance.right === "none";
  // The stage itself being blocked. Named apart from `refused` on purpose:
  // one is "you may not", the other is "nobody may, yet". Both stop an
  // advance, for completely different reasons.
  // `Boolean(...)`, not `!== null`, and that is not a style preference.
  //
  // The API dropped this field entirely for a day. `undefined !== null` is
  // TRUE, so every stage reported itself blocked and the Block button stopped
  // rendering -- while anything asking `stage.block ? ...` saw a falsy value
  // and showed nothing. **The two conditions disagreed**, which is why the
  // screen looked impossible rather than broken.
  //
  // One truthiness test everywhere means a missing field fails the same way
  // in every place at once, which is diagnosable. The real fix is `toStageView`
  // in the API; this is the half that makes the next omission legible.
  const isBlocked = Boolean(stage.block);
  // Underway and not yet finished: nothing finishes a stage twice. Complete
  // was drawn on a complete end stage too until 22 Oct 2026.
  const underway = stage.state === "in_progress";
  // Start is not here: it sits beside the stage name since punch list #8.
  return (
    <div className="row">
      {underway && outcomes.length === 0 ? (
        <button
          className="primary"
          onClick={() => {
            const finish = { outcome: COMPLETE, toStageName: "", isRework: false, unready: null, pastDue: null };
            if (needsPrompt(finish, advance)) onAsk(finish);
            else onComplete();
          }}
          disabled={busy || refused || isBlocked}
        >
          Complete
        </button>
      ) : null}

      {underway && outcomes.length > 0
        ? outcomes.map((o) => (
            <button
              key={o.outcome}
              // "Lost" is a finish, but not a go: drawn apart from Won so the
              // two are not read alike (punch list #53, 20 Oct 2026). Matched
              // by name - the Proposal workflow's only negative ending.
              className={o.isRework ? "rework" : o.outcome === "Lost" ? "lost" : "primary"}
              onClick={() => (needsPrompt(o, advance) ? onAsk(o) : onComplete(o.outcome))}
              // Into an unready stage, for somebody who may not move it on
              // anyway: inert, and the line below says why and who can.
              disabled={busy || refused || isBlocked || (o.unready !== null && !advance.mayOverrideUnready)}
              title={
                o.unready
                  ? `${o.toStageName} has ${o.unready}`
                  : o.pastDue
                    ? `${o.toStageName} was due ${mdy(o.pastDue)}`
                    : undefined
              }
            >
              {o.outcome}
              {/* Where it goes, at full strength since 30 Oct 2026 (punch list
                  F147): faint, "Approved" read as the whole button and the
                  work landed on Final review to everyone's surprise. */}
              <span className="outcome-to">→ {o.toStageName}</span>
            </button>
          ))
        : null}

      {/* Blocking no longer depends on the stage being underway. A stage can
          be held before anyone starts it, which is the whole point of the
          change -- and `state` no longer carries blocked-ness at all. */}
      {!isBlocked && stage.state !== "complete" ? (
        <button onClick={onAskBlock} disabled={busy}>
          Block
        </button>
      ) : null}
      {isBlocked ? (
        /* "Wake" rather than "Unblock" when it is hibernated. Releasing does
           both -- hibernation lives ON the block, so there is only ever
           one control -- but a button reading "Unblock" on a report
           nobody has looked at for a month does not say what pressing it
           will put back on their list. */
        <button onClick={() => onBlock(false)} disabled={busy}>
          {stage.block?.hibernatedAt ? "Wake" : "Unblock"}
        </button>
      ) : null}
    </div>
  );
}

/** " · Overdue 2 days" in the late colour, or nothing (punch list F150). */
function OverdueMark({ days }: { days: number | null }) {
  return days === null ? null : <span className="overdue-mark"> · {overdueText(days)}</span>;
}

/**
 * Every stage's instructions on one scrolling sheet.
 *
 * The tabs are right for doing the work — one stage at a time, with the
 * actions for it. They are wrong for picking a job up cold, where what you
 * need is the shape of the whole thing before you start any of it. Ten clicks
 * to read ten paragraphs is how people end up not reading them.
 *
 * Deliberately read-only. Editing happens on the stage itself, where the due
 * date and the people are visible too and a change has its context.
 */
/**
 * How a stage tab reads: its words and its colour class (punch list #9).
 *
 * BLOCKED comes from the block, never the state -- a block stopped being a
 * state on 10 Oct. SKIPPED is a stage never entered on a task that has
 * finished: the route went another way (Author edits when the review
 * passed). Before the task finishes, an unentered stage may still be reached,
 * so it reads Not started rather than guessing.
 */
function stageLook(
  stage: TaskStageView,
  /** The task is Done, or the work went past this stage without entering it. */
  taskDone: boolean,
): { readonly label: string; readonly className: string } {
  if (stage.block) {
    return stage.block.hibernatedAt
      ? { label: "Hibernated", className: "blocked" }
      : { label: "Blocked", className: "blocked" };
  }
  switch (stage.state) {
    case "complete":
      return { label: "Done", className: "complete" };
    case "in_progress":
      return { label: "In progress", className: "in_progress" };
    case "not_started":
      return taskDone
        ? { label: "Skipped", className: "not_started skipped" }
        : { label: "Not started", className: "not_started" };
  }
}

function AllInstructions({
  stages,
  inDialog = false,
}: {
  stages: readonly TaskStageView[];
  /** In a `Modal`, which supplies the frame, the heading and the scrolling. */
  inDialog?: boolean;
}) {
  const written = stages.filter((s) => s.description?.trim());

  return (
    <div className={inDialog ? "all-steps in-dialog" : "card all-steps"}>
      {inDialog ? (
        <p className="muted" style={{ marginTop: 0 }}>
          {written.length} of {stages.length} stages have any
        </p>
      ) : (
        <div className="section-head" style={{ marginTop: 0 }}>
          <h2>All instructions</h2>
          <span className="count">
            {written.length} of {stages.length} stages have any
          </span>
        </div>
      )}

      <div className={inDialog ? undefined : "all-steps-scroll"}>
        {stages.map((s) => (
          <div
            key={s.id}
            className={`all-step ${s.state} ${s.isCurrent ? "current" : ""}`}
          >
            <div className="all-step-head">
              <strong>{s.name}</strong>
              <span className={`state ${s.state}`}>{s.state.replace("_", " ")}</span>
              {s.isCurrent ? <span className="chip">here now</span> : null}
              <span className="muted">
                {s.assignees.length > 0
                  ? s.assignees.map((a) => a.label).join(", ")
                  : "Unassigned"}
                {s.dueDate ? ` · due ${mdy(s.dueDate)}` : ""}
              </span>
            </div>

            {s.description?.trim() ? (
              <div className="all-step-body">
                {/* Read-only here, so the boxes render disabled rather than
                    inviting a click that does nothing. */}
                <RichText text={s.description} />
              </div>
            ) : (
              <div className="all-step-body muted">No instructions written.</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * One line of the audit trail, with the detail tucked away.
 *
 * Instructions can run to a paragraph with a checklist in it, and printing
 * the old and new text inline turned this list into a wall. The log's job is
 * to say WHO CHANGED WHAT AND WHEN at a glance; the exact words are what you
 * want second, and only for the one row you are asking about.
 */
function ActivityRowView({ row }: { row: ActivityRow }) {
  const [open, setOpen] = useState(false);
  const bulky = isBulky(row);
  // A rule's row names the rule, never "Someone" (ruleActors.ts).
  const rule = row.byRule ? ruleActivity(row, row.byRule) : null;

  return (
    <li>
      <strong>{rule ? rule.lead : (row.actorName ?? "Someone")}</strong>{" "}
      {rule?.rest ?? (bulky ? describeBriefly(row) : describeActivity(row))}
      <span className="muted"> · {whenLabel(row.at)}</span>

      {bulky ? (
        <>
          {" "}
          <button className="linkish" onClick={() => setOpen((v) => !v)}>
            {open ? "hide" : "show change"}
          </button>

          {open ? (
            <div className="change">
              <div className="change-side">
                <span className="change-tag">Before</span>
                <div className="change-body">
                  {row.previousValue?.trim() ? row.previousValue : "— empty —"}
                </div>
              </div>
              <div className="change-side">
                <span className="change-tag">After</span>
                <div className="change-body">
                  {row.newValue?.trim() ? row.newValue : "— empty —"}
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </li>
  );
}

/**
 * Whether a change is too big to read inline.
 *
 * Length or a line break, not the field name: a one-word instruction should
 * still read inline, and a title long enough to swamp the row should not.
 */
function isBulky(row: ActivityRow): boolean {
  if (row.action !== "changed") return false;
  const longest = Math.max(
    row.previousValue?.length ?? 0,
    row.newValue?.length ?? 0,
  );
  const wrapped =
    (row.previousValue?.includes("\n") ?? false) ||
    (row.newValue?.includes("\n") ?? false);
  return wrapped || longest > 60;
}

/** "changed the instructions on PM review" — the values stay hidden. */
function describeBriefly(row: ActivityRow): string {
  const where = row.stageName ? ` on ${row.stageName}` : "";
  const label = row.field ? `the ${fieldLabel(row.field)}` : "something";

  const had = Boolean(row.previousValue?.trim());
  const has = Boolean(row.newValue?.trim());
  if (!had && has) return `wrote ${label}${where}`;
  if (had && !has) return `cleared ${label}${where}`;
  return `changed ${label}${where}`;
}

/**
 * Who is on this task, at a glance, without opening ten tabs.
 *
 * The question "who is involved in this" had no answer short of clicking
 * through every stage. It is the first thing a PM asks about somebody else's
 * task and the first thing anyone asks when work is handed to them.
 *
 * Only stages that have somebody on them are listed. An unassigned stage is
 * usually one the work has not reached and nobody has thought about yet, and
 * a roster padded with "Finalize — nobody" is a roster people stop reading.
 * The count says how many were left out, so a short list is never mistaken
 * for the whole picture.
 */
/**
 * "2026-10-08" to "10/08/26", on the STRING.
 *
 * Deliberately not via `new Date(...)`: a date-only string is parsed as UTC and
 * rendered in local time, which shows the previous day for anyone west of UTC.
 * A stage due date is a calendar day, not an instant.
 */
/**
 * Where the stage stands on snoozes, said in the dialog (hold row 6b) -- the
 * same answer the server enforces, so the date offered is a date it accepts.
 */
function SnoozeLine({ position }: { position: BlockOptions["snooze"] }) {
  if (position.kind === "used_up") return null;
  if (position.kind === "matrix") {
    return (
      <p className="muted">
        Snooze {position.next} of {position.of} here: {position.minDays}–{position.maxDays}{" "}
        business days
        {position.earliest && position.latest
          ? ` — between ${mdy(position.earliest)} and ${mdy(position.latest)}.`
          : "."}
      </p>
    );
  }
  return (
    <p className="muted">
      Up to {position.maxDays} business days for {position.as}
      {position.latest ? ` — no later than ${mdy(position.latest)}.` : "."}
    </p>
  );
}

/** The final stage's one "outcome", which is completing it. */
const COMPLETE = "Complete";

/**
 * Whether an outcome needs the prompt before it can go: a reason to send work
 * back, a note when somebody steps in for the holder, the override into an
 * unready stage (E7.9), or acknowledging that the next stage's date has
 * passed (E11.5). Anything else goes straight through.
 */
function needsPrompt(o: StageOutcome, advance: AdvancePermission): boolean {
  return o.isRework || advance.needsNote || o.unready !== null || o.pastDue !== null;
}

/**
 * The ONE prompt for an outcome: every question it needs, on one form.
 */
function AdvancePrompt({
  stageId,
  outcome,
  advance,
  reason,
  note,
  busy,
  onReason,
  onNote,
  onCancel,
  onConfirm,
}: {
  stageId: string;
  outcome: StageOutcome;
  advance: AdvancePermission;
  reason: string;
  note: string;
  busy: boolean;
  onReason: (v: string) => void;
  onNote: (v: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const needsReason = outcome.isRework;
  const overriding = outcome.unready !== null;
  const needsNote = advance.needsNote || overriding;
  // E11.5: a warning, not a refusal -- but a real acknowledgement, ticked,
  // not one assumed from pressing a button that might have meant anything.
  const pastDue = outcome.pastDue;
  const [acknowledged, setAcknowledged] = useState(false);
  const ready =
    (!needsReason || reason.trim() !== "") &&
    (!needsNote || note.trim() !== "") &&
    (pastDue === null || acknowledged);
  const label = outcome.outcome === COMPLETE ? "Complete" : outcome.outcome;
  return (
    <div className="rework-prompt">
      {pastDue ? (
        <div className="warn" style={{ marginBottom: 10 }}>
          <label className="past-due-ack">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <span>
              <strong>{outcome.toStageName}</strong> was due on {mdy(pastDue)}, which has passed. I
              understand, and am moving the work on anyway — this is recorded on the task.
            </span>
          </label>
        </div>
      ) : null}
      {needsReason ? (
        <div className="field">
          <label htmlFor={`reason-${stageId}`}>Why is this going back to {outcome.toStageName}?</label>
          <textarea
            id={`reason-${stageId}`}
            value={reason}
            onChange={(e) => onReason(e.target.value)}
            placeholder="What needs changing, and why"
            autoFocus
          />
        </div>
      ) : null}
      {needsNote ? (
        <div className="field">
          <label htmlFor={`pm-${stageId}`}>
            {overriding
              ? `${outcome.toStageName} has ${outcome.unready}. Move the work on anyway — why?`
              : "You are moving this on for someone else. Why?"}
          </label>
          <textarea
            id={`pm-${stageId}`}
            value={note}
            onChange={(e) => onNote(e.target.value)}
            placeholder={
              overriding
                ? "Whoever picks it up is being confirmed this week"
                : "Jacob is on site this week and this cannot wait"
            }
            autoFocus={!needsReason}
          />
        </div>
      ) : null}
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className={needsReason ? "rework" : "primary"} disabled={busy || !ready} onClick={onConfirm}>
          {/* One plain verb (punch list #68, 20 Oct 2026). It read
              "<outcome> anyway" or "<outcome> as PM"; the line above already
              says what is being acknowledged and why, so the button only
              has to say what happens. */}
          {needsReason ? "Send back" : outcome.outcome === COMPLETE ? label : "Move on"}
        </button>
      </div>
    </div>
  );
}

/**
 * The task's name, renamed in place (punch list #14, Jeremy 12 Oct). The EDIT
 * chip shows only to the people the server lets rename it -- the owner, the
 * deliverable's PM, an admin (`editability.handOn`) -- so nobody is offered a
 * box that would refuse them. The rename is recorded in "Who changed what".
 */
function TaskTitle({
  title,
  mayRename,
  busy,
  onRename,
}: {
  title: string;
  mayRename: boolean;
  busy: boolean;
  /** Resolves TRUE only when it saved, so a refusal keeps what was typed. */
  onRename: (title: string) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  useEffect(() => setDraft(title), [title]);

  if (!editing) {
    return (
      <h1 className="task-h" style={{ marginTop: 0 }}>
        <KindTag kind="task" size="head" />
        {title}
        {mayRename ? (
          <EditChip title="Rename this task" onClick={() => setEditing(true)} />
        ) : null}
        <HelpButton screens="task" />
      </h1>
    );
  }

  const next = draft.trim();
  const save = async (): Promise<void> => {
    if (next === "" || next === title) {
      setEditing(false);
      return;
    }
    if (await onRename(next)) setEditing(false);
  };
  return (
    <form
      className="task-rename"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <KindTag kind="task" size="head" />
      <input
        aria-label="Task name"
        value={draft}
        maxLength={300}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setDraft(title);
            setEditing(false);
          }
        }}
      />
      <button type="button" disabled={busy} onClick={() => { setDraft(title); setEditing(false); }}>
        Cancel
      </button>
      <button type="submit" className="primary" disabled={busy || next === ""}>
        Save
      </button>
    </form>
  );
}

/**
 * An ADMIN moves the task back a stage, undoing a misclick (E5a) -- and, since
 * 30 Oct 2026, whoever made the last move, until the next stage is started
 * (F116). Shown only when the server says so (`editability.reverse`). Opened
 * from Move back a stage on the stage list's row of the stage the work would go
 * back to, as a pop-up since F169 (Jeremy, 30 Oct 2026: "make the explanation
 * box pop out"). Asks for a note, because the reversal is recorded with it --
 * "a note, not a dropdown" (Jeremy).
 */
function MoveBackDialog({
  busy,
  onMoveBack,
  onCancel,
}: {
  busy: boolean;
  onMoveBack: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState("");
  return (
    <Modal title="Move back a stage" onCancel={onCancel}>
      <div className="field">
        <label htmlFor="move-back-note">Why is it being moved back?</label>
        <textarea
          id="move-back-note"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Advanced by mistake — it was not ready for review"
          autoFocus
        />
      </div>
      <div className="row">
        <button
          className="rework"
          disabled={busy || note.trim() === ""}
          onClick={() => onMoveBack(note.trim())}
        >
          Move back
        </button>
        <button onClick={onCancel} disabled={busy}>
          Keep it here
        </button>
      </div>
    </Modal>
  );
}

/**
 * Asks to move a date (E5.2) and their answers (E5.3). Open ones first, each
 * with Approve / Reject for whoever may decide; the rest say who decided and
 * any note, beside the history of why dates moved.
 */
function DateRequests({
  requests,
  mayDecide,
  mayApprove,
  busy,
  onDecide,
}: {
  requests: readonly DateChangeRequestView[];
  mayDecide: boolean;
  /** False on a cancelled task, where an ask can only be rejected. */
  mayApprove: boolean;
  busy: boolean;
  onDecide: (id: string, approve: boolean, note: string | null) => void;
}) {
  const [notes, setNotes] = useState<Record<string, string>>({});
  return (
    <section className="side-section">
      <div className="section-label">
        <span>Date requests</span>
      </div>
      <ul className="history">
        {requests.map((r) => (
          <li key={r.id}>
            <strong>{r.stageName ?? "Date to the client"}</strong>:{" "}
            {r.previousDate ? mdy(r.previousDate) : "not set"} → {r.requestedDate ? mdy(r.requestedDate) : "cleared"}{" "}
            {r.reasonCategory ? <span className="chip">{r.reasonCategory}</span> : null}
            {r.note ? <span className="muted"> {r.note}</span> : null}
            {r.requestedBy ? <span className="muted"> · asked by {r.requestedBy}</span> : null}
            {r.decision === null ? (
              mayDecide ? (
                <div className="row" style={{ marginTop: 6 }}>
                  <input
                    aria-label="Note for the person who asked"
                    placeholder="Note (optional)"
                    value={notes[r.id] ?? ""}
                    onChange={(e) => setNotes({ ...notes, [r.id]: e.target.value })}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                  {mayApprove ? (
                    <button
                      className="primary"
                      disabled={busy}
                      onClick={() => onDecide(r.id, true, notes[r.id]?.trim() || null)}
                    >
                      Approve
                    </button>
                  ) : null}
                  <button
                    disabled={busy}
                    onClick={() => onDecide(r.id, false, notes[r.id]?.trim() || null)}
                  >
                    Reject
                  </button>
                </div>
              ) : (
                <span className="chip"> waiting for the PM</span>
              )
            ) : (
              <span className="muted">
                {" "}
                · {r.decision === "approved" ? "approved" : "rejected"}
                {r.decidedBy ? ` by ${r.decidedBy}` : ""}
                {r.decisionNote ? ` — ${r.decisionNote}` : ""}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function WhoIsOn({
  fetcher,
  taskId,
  meKey,
  ownerKey,
  ownerName,
  assigneeName,
  mayChangeOwner,
  onPlan,
  stages,
  workflowName,
  moveBackStageId,
  onMoveBack,
  busy,
  onChanged,
}: {
  fetcher: Fetcher;
  taskId: string;
  /**
   * The signed-in person's Ajera key, or null for somebody with no Deltek
   * record. **A KEY, never a name**: two people can share a display name and
   * a name can change, so matching on one would eventually highlight the
   * wrong person — which is the single thing this cue must not do.
   */
  meKey: string | null;
  ownerKey: string | null;
  ownerName: string | null;
  /**
   * Who a SIMPLE task is assigned to (`tasks.assignee_key`), by name. On a
   * workflow task the stages say who has it, so it is not shown there.
   */
  assigneeName: string | null;
  /**
   * May hand the task to another PM / TM (`editability.handOn`): its owner,
   * its deliverable's PM or an admin. CHANGE is offered to nobody else -- the
   * server refuses them (`mayHandOnTask`).
   */
  mayChangeOwner: boolean;
  /**
   * Opens Plan all stages, offered here beside the PM / TM since 30 Oct 2026
   * (punch list F121; it sat under the stage panel). Null for anybody Deltek
   * does not flag PM or admin -- the server refuses Plan's saves from them --
   * and on a simple or cancelled task.
   */
  onPlan: (() => void) | null;
  stages: readonly TaskStageView[];
  workflowName: string | null;
  /**
   * The stage a move back would reopen, when this person may move the work
   * back (F169): Move back a stage sits on its row. Null otherwise.
   */
  moveBackStageId: string | null;
  onMoveBack: () => void;
  busy: boolean;
  onChanged: () => void;
}) {
  const [editingOwner, setEditingOwner] = useState(false);
  const [managers, setManagers] = useState<ReferenceOption[]>([]);
  const [ownerError, setOwnerError] = useState<string | null>(null);

  // Only PM-flagged people. Ownership carries the right to advance any
  // stage, so the list is the one Ajera says may hold it.
  useEffect(() => {
    if (!editingOwner || managers.length > 0) return;
    void api.projectManagers(fetcher).then(setManagers).catch(() => undefined);
  }, [editingOwner, managers.length, fetcher]);

  // A refusal is SHOWN and the picker stays open. It was swallowed until
  // 22 Oct 2026, so a refused change closed the picker and looked like a
  // click that did nothing.
  const setOwner = async (key: string): Promise<void> => {
    setOwnerError(null);
    try {
      await api.updateTask(fetcher, taskId, { ownerKey: key === "" ? null : ajeraKey(key) });
    } catch (e) {
      setOwnerError(e instanceof ApiError ? e.message : String(e));
      return;
    }
    setEditingOwner(false);
    onChanged();
  };
  const empty = stages.filter((s) => s.assignees.length === 0).length;
  // The stage Move back a stage goes on is listed even with nobody on it, so
  // the button always has its row (F169).
  const staffed = stages.filter((s) => s.assignees.length > 0 || s.id === moveBackStageId);

  return (
    <div className={workflowName ? "roster" : "roster simple"}>
      <div className="roster-owner">
        <span className="roster-label">PM / TM</span>
        {editingOwner ? (
          <span className="row" style={{ display: "inline-flex", gap: 8 }}>
            <SearchSelect
              id="owner-pick"
              options={managers}
              value={ownerKey ?? ""}
              onChange={(key) => void setOwner(key)}
              emptyLabel="Start typing a project manager"
            />
            <button
              type="button"
              onClick={() => {
                setEditingOwner(false);
                setOwnerError(null);
              }}
            >
              Cancel
            </button>
          </span>
        ) : (
          <>
            <strong>{ownerName ?? "nobody"}</strong>
            <span className="muted"> · accountable end to end</span>
            {/* CHANGE rather than EDIT: the thing being changed is a PERSON,
                and "edit the owner" reads as editing them rather than
                replacing them. Same chip, same handler, one different word. */}
            {mayChangeOwner ? (
              <EditChip
                label="CHANGE"
                title="Change who owns this task"
                onClick={() => setEditingOwner(true)}
              />
            ) : null}
            {onPlan ? (
              <button type="button" className="linkish roster-plan" onClick={onPlan}>
                Plan all stages
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* Said where the change is made, because the consequence is not
          obvious: the owner is the one person who can move any stage. */}
      {editingOwner ? (
        <p className="muted" style={{ margin: "4px 0 8px" }}>
          The PM / Task Manager can advance any stage of this task, and is told
          when they are given it.
        </p>
      ) : null}
      {ownerError ? <div className="error">{ownerError}</div> : null}

      {/* A simple task has no stages to say who has it; its assignee is the
          answer, and the page never showed it until 22 Oct 2026. */}
      {!workflowName ? (
        <div className="roster-owner">
          <span className="roster-label">Assigned to</span>
          <strong>{assigneeName ?? "nobody"}</strong>
        </div>
      ) : null}

      {staffed.length > 0 ? (
        <ul className="roster-list">
          {staffed.map((s) => (
            <li key={s.id} className={s.isCurrent ? "here" : ""}>
              <span className="roster-stage">
                {s.name}
                {s.isCurrent ? <span className="chip">here now</span> : null}
                {s.id === moveBackStageId ? (
                  <button
                    type="button"
                    className="linkish roster-move-back"
                    onClick={onMoveBack}
                    disabled={busy}
                  >
                    Move back a stage
                  </button>
                ) : null}
              </span>
              <span className="roster-people">
                {/* Each name separately rather than one joined string, so the
                    person's own name can be marked without marking the people
                    beside them. */}
                {s.assignees.map((a, i) => (
                  <span key={a.key}>
                    {i > 0 ? ", " : ""}
                    <span className={a.key === meKey ? "mine-name" : undefined}>
                      {a.label}
                    </span>
                  </span>
                ))}
                {/* After the names, not beside the stage: it kept the stage
                    column wide and cramped. Each part is omitted when unset
                    rather than shown empty — a blank due date means nobody has
                    planned that stage and null hours mean nobody estimated it,
                    so printing "—" or "0 h" would turn "no answer" into an
                    answer. */}
                {s.dueDate ? <span className="roster-due">{mdy(s.dueDate)}</span> : null}
                {s.allocatedHours !== null ? (
                  <span className="roster-hrs">
                    {s.dueDate ? "- " : ""}
                    {Number.isInteger(s.allocatedHours)
                      ? s.allocatedHours
                      : s.allocatedHours.toFixed(1)}
                    {" h"}
                  </span>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      ) : workflowName ? (
        <p className="muted" style={{ margin: "6px 0 0" }}>
          No stage has anyone on it yet.
        </p>
      ) : null}

      {empty > 0 ? (
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
          {empty} {empty === 1 ? "stage has" : "stages have"} nobody assigned yet.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Where the crew is going, and when.
 *
 * Only on field work. WHEN is the task's resource-scheduler BOOKING since 21 Oct 2026 (Jeremy:
 * "one stage, one owner"): linked, it brings the dates, and the Mobilize & collect data stage
 * takes its dates, crew and hours from it -- changed only in the scheduler. WHERE (county and
 * state) stays here. All are required before the task can leave Planning rather than before it
 * can exist, because Planning is where the answers come from. The panel says which are still
 * missing, so the requirement is visible before it becomes a refusal.
 *
 * Opening the task brings a linked one up to date with its booking; the scheduler being down
 * never unlinks it, and the panel says when it could not check.
 */
function Mobilization({
  fetcher,
  task,
  mayLink,
  cancelled,
  onChanged,
}: {
  fetcher: Fetcher;
  task: TaskView;
  /** Whoever may hand the task on may link or unlink its booking (`editability.handOn`). */
  mayLink: boolean;
  /** Cancelled: where and when are shown as they stood, and nothing is saved. */
  cancelled: boolean;
  onChanged: () => void;
}) {
  const [states, setStates] = useState<{ code: string; name: string }[]>([]);
  const [county, setCounty] = useState(task.county ?? "");
  const [stateCode, setStateCode] = useState(task.stateCode ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [stale, setStale] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [options, setOptions] = useState<
    { available: true; options: DeploymentOption[] } | { available: false; reason: string } | null
  >(null);
  const linked = task.schedulerMobilizationId;

  useEffect(() => {
    void api.states(fetcher).then(setStates).catch(() => undefined);
  }, [fetcher]);

  useEffect(() => {
    setCounty(task.county ?? "");
    setStateCode(task.stateCode ?? "");
  }, [task]);

  // A linked task is brought up to date with its booking whenever it is opened.
  useEffect(() => {
    if (!linked) return;
    let gone = false;
    api.refreshDeployment(fetcher, task.id).then(
      (r) => {
        if (gone) return;
        setStale(r.available ? null : r.reason ?? "The resource scheduler could not be read.");
        if (r.refreshed > 0 || r.lost > 0) onChanged();
      },
      // SAID, not swallowed: a refresh that failed for any reason leaves the panel showing what
      // the booking last said, and the person should know that. Swallowing it hid a refresh that
      // never worked (21 Oct 2026).
      (e) => {
        if (!gone) setStale(e instanceof ApiError ? e.message : "The refresh failed.");
      },
    );
    return () => {
      gone = true;
    };
    // Once per task and link, not on every reload it causes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetcher, task.id, linked]);

  const dirty = county !== (task.county ?? "") || stateCode !== (task.stateCode ?? "");
  const missing = [
    !linked && "booking",
    !task.county?.trim() && "county",
    !task.stateCode && "state",
  ].filter(Boolean) as string[];

  const run = async (work: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const openPicker = (): void => {
    setPicking(true);
    setOptions(null);
    void api.deploymentOptions(fetcher, task.id).then(setOptions, (e) =>
      setOptions({ available: false, reason: e instanceof ApiError ? e.message : String(e) }),
    );
  };

  const link = (option: DeploymentOption): Promise<void> =>
    run(async () => {
      const r = await api.linkDeployment(fetcher, task.id, option.id);
      setPicking(false);
      setNotice(
        r.unknownCrew.length > 0
          ? `Linked. ${r.unknownCrew.length} of the crew are not in Fieldpath's roster yet and will be added once it catches up (within the hour).`
          : null,
      );
      onChanged();
    });

  const unlink = (): Promise<void> =>
    run(async () => {
      if (!window.confirm("Unlink this booking? The stage keeps its dates, crew and hours, and they become editable here again. The booking in the scheduler is not touched.")) return;
      await api.unlinkDeployment(fetcher, task.id);
      onChanged();
    });

  const saveWhere = (): Promise<void> =>
    run(async () => {
      await api.updateTask(fetcher, task.id, { county: county.trim() || null, stateCode: stateCode || null });
      onChanged();
    });

  return (
    <div className="card">
      <div className="section-head" style={{ marginTop: 0 }}>
        <h2>Mobilization</h2>
        <span className="count">{missing.length === 0 ? "complete" : `${missing.join(", ")} still needed`}</span>
      </div>

      {missing.length > 0 && !task.mobilizationLost && !cancelled ? (
        <div className="warn">Field work cannot leave Planning until a booking is linked and the county and state are set.</div>
      ) : null}
      {task.mobilizationLost && !linked ? (
        <div className="warn">{task.mobilizationLost} Link another booking to carry on.</div>
      ) : null}
      {error ? <div className="error">{error}</div> : null}
      {notice ? <div className="warn">{notice}</div> : null}

      <div className={`pending-link${linked ? " booking-linked" : ""}`}>
        <span className="roster-label">Booking</span>
        {linked ? (
          <>
            <span>
              {task.mobilizationStart ? mdy(task.mobilizationStart) : "?"}
              {task.mobilizationEnd && task.mobilizationEnd !== task.mobilizationStart ? ` – ${mdy(task.mobilizationEnd)}` : ""}
            </span>
            {/* Booked when saved: field work has no approval step (Jeremy, 27 Oct 2026). */}
            <span className="chip completed">Booked</span>
            <a href={`${schedulerUrl}/deployments/${linked}`} target="_blank" rel="noreferrer">
              Open in scheduler<span aria-hidden="true"> ↗</span>
            </a>
            {mayLink ? (
              <button type="button" className="booking-unlink" disabled={busy} onClick={() => void unlink()}>Unlink</button>
            ) : null}
            <span className="muted booking-note">
              Mobilize &amp; collect data takes its dates, crew and hours from this booking. Change them in the scheduler.
            </span>
            {stale ? <span className="booking-note warn-text">Could not check the scheduler just now: {stale} Showing what it last said.</span> : null}
          </>
        ) : (
          <>
            <span className="muted">No booking linked. Book the trip in the scheduler, then link it here.</span>
            {mayLink && !picking ? (
              <button type="button" disabled={busy} onClick={openPicker}>Link a booking</button>
            ) : null}
            <a href={`${schedulerUrl}/field`} target="_blank" rel="noreferrer">
              Book it in the scheduler<span aria-hidden="true"> ↗</span>
            </a>
          </>
        )}
      </div>

      {picking && !linked ? (
        <div className="booking-picker">
          {options === null ? (
            <p className="muted">Reading this project's bookings from the scheduler…</p>
          ) : !options.available ? (
            <div className="warn">{options.reason}</div>
          ) : options.options.length === 0 ? (
            <p className="muted">This project's Ajera job has no live booking that another task has not already linked.</p>
          ) : (
            <ul className="booking-options">
              {options.options.map((o) => (
                <li key={o.id}>
                  <span className="booking-title">{o.title}</span>
                  <span className="muted">
                    {mdyRange(o.start, o.end)} · {o.crew.join(", ") || "no crew"}{o.site ? ` · ${o.site}` : ""}
                  </span>
                  <button type="button" className="primary" disabled={busy} onClick={() => void link(o)}>Link</button>
                </li>
              ))}
            </ul>
          )}
          <div className="row" style={{ justifyContent: "flex-end" }}>
            <button type="button" disabled={busy} onClick={() => setPicking(false)}>Never mind</button>
          </div>
        </div>
      ) : null}

      {/* Cancelled: said as it stood, in words. A disabled box looks like
          one that can be typed in, which is the refusal-after-the-fact this
          page avoids. */}
      {cancelled ? (
        <p style={{ margin: "10px 0 0" }}>
          <span className="roster-label">Where </span>
          {[task.county?.trim(), states.find((s) => s.code === task.stateCode)?.name ?? task.stateCode]
            .filter(Boolean)
            .join(", ") || <span className="muted">not set</span>}
        </p>
      ) : (
        <div className="mob-grid">
          <div className="field">
            <label htmlFor="mob-county">County</label>
            <input id="mob-county" value={county} onChange={(e) => setCounty(e.target.value)} placeholder="Brown" />
          </div>
          <div className="field">
            <label htmlFor="mob-state">State</label>
            <select id="mob-state" value={stateCode} onChange={(e) => setStateCode(e.target.value)}>
              <option value="">Choose a state…</option>
              {states.map((s) => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {cancelled ? null : (
        <div className="row" style={{ justifyContent: "flex-end" }}>
          {dirty ? (
            <button disabled={busy} onClick={() => { setCounty(task.county ?? ""); setStateCode(task.stateCode ?? ""); }}>Discard</button>
          ) : null}
          <button className="primary" disabled={busy || !dirty} onClick={() => void saveWhere()}>
            {busy ? "Saving…" : "Save where"}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * What a field is called to a person.
 *
 * The audit log stores the internal name, and rows written before a rename
 * still say the old one. Mapping at READ time means history keeps reading
 * correctly instead of needing a migration every time wording changes —
 * "owner" is what the column has always been called; "PM / Task Manager" is
 * what everyone at ERP calls the person.
 */
function fieldLabel(field: string): string {
  return field === "owner" ? "PM / Task Manager" : field;
}

/**
 * Which task, and which stage of it, a block waits on.
 *
 * **One component for both places that choose a target** -- the Block dialog
 * and the Link it control on an existing block. Two copies of these selects is
 * how the dialog would come to offer one set of tasks and the control another,
 * with each looking right on its own.
 *
 * It owns no state. Choosing a task clears the stage, because the stage
 * belongs to the task and keeping a stale one would point the block at a stage
 * of a task nobody chose.
 */
function BlockTargetPicker({
  idPrefix,
  targets,
  taskId,
  stageId,
  onTask,
  onStage,
  emptyHint,
}: {
  idPrefix: string;
  /** Null while loading. */
  targets: readonly BlockTargetOption[] | null;
  taskId: string | null;
  stageId: string | null;
  onTask: (taskId: string | null) => void;
  onStage: (stageId: string | null) => void;
  /** What the person can still do when nothing can be waited on. */
  emptyHint: string;
}) {
  if (targets === null) {
    return <p className="muted">Looking for what this could wait on…</p>;
  }
  if (targets.length === 0) {
    /* An empty list is a FACT, not a failure, and it has to say which. Nothing
       else in this project shares this deliverable and no project-wide work
       exists yet. */
    return (
      <p className="muted">
        Nothing in this project can be waited on yet — no other task shares this
        deliverable, and there is no project-wide work. {emptyHint}
      </p>
    );
  }

  const chosenTask = targets.find((t) => t.taskId === taskId) ?? null;

  return (
    <>
      <div className="field">
        <label htmlFor={`${idPrefix}-task`}>Which task?</label>
        <select
          id={`${idPrefix}-task`}
          value={taskId ?? ""}
          onChange={(e) => {
            onTask(e.target.value || null);
            onStage(null);
          }}
        >
          <option value="">Not linked yet</option>
          {/* TWO GROUPS, because a task from ANOTHER deliverable appearing
              here looks like a bug unless something says why it is offered. */}
          <optgroup label="In this deliverable">
            {targets
              .filter((t) => !t.isProjectLevel)
              .map((t) => (
                <option key={t.taskId} value={t.taskId}>
                  {t.taskTitle}
                </option>
              ))}
          </optgroup>
          <optgroup label="Project-wide work">
            {targets
              .filter((t) => t.isProjectLevel)
              .map((t) => (
                <option key={t.taskId} value={t.taskId}>
                  {t.taskTitle}
                  {t.deliverableName ? ` — ${t.deliverableName}` : ""}
                </option>
              ))}
          </optgroup>
        </select>
      </div>

      <div className="field">
        <label htmlFor={`${idPrefix}-stage`}>Which stage of it?</label>
        <select
          id={`${idPrefix}-stage`}
          value={stageId ?? ""}
          disabled={!chosenTask}
          onChange={(e) => onStage(e.target.value || null)}
        >
          <option value="">{chosenTask ? "Choose a stage" : "Choose a task first"}</option>
          {chosenTask?.stages.map((st) => (
            <option key={st.id} value={st.id}>
              {st.name}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
